// The game's aircraft. Each class flies an art/ model (aircraft_models.js,
// model-integration.md Part 4) and keeps its procedural voxel silhouette —
// chunky merged boxes — as the far level of detail and the fallback for a
// model that hasn't loaded. Both are built nose-along-+X / up-+Y /
// right-wing-+Z so server attitude quaternions apply directly to the mesh, and
// each class gets its own silhouette so you can read the matchup at a glance.

import * as THREE from "three";

import { TEAM_INFO, TEAM_CRIMSON, TEAM_COBALT } from "./teams.js";
import { loadModel, modelIfReady, modelsEnabled, ModelLOD, setLodLevels } from "./models.js";
import { insigniaImage, insigniaURL } from "./insignia.js";
import { muzzleFlash } from "./flipbooks.js";
import { batteryMuzzles } from "./battery.js";
import {
  AircraftRig,
  AIRCRAFT_MODELS,
  CLASS_MODEL,
  ERA_MODEL,
  finStripeBoxes,
  modelFamily,
  modelForClass,
} from "./aircraft_models.js";

export { TEAM_CRIMSON, TEAM_COBALT, TEAM_INFO } from "./teams.js";

/** Merge axis-aligned colored boxes into one vertex-colored geometry.
 *  An entry may instead be a smooth cone/cylinder segment lying along +X:
 *  { cone: true, x,y,z, len, r0, r1, seg?, color } — r0 is the aft (-X)
 *  radius, r1 the forward (+X) radius (0 for a sharp tip). Used sparingly
 *  (the jet's radome, tip tanks) so the chunky-box look stays the rule. */
export function mergeBoxes(boxes) {
  const positions = [];
  const normals = [];
  const colors = [];
  const c = new THREE.Color();
  for (const b of boxes) {
    let g;
    if (b.cone) {
      // CylinderGeometry's +Y top carries radiusTop; rotate +Y onto +X so
      // r1 faces forward along the nose line.
      g = new THREE.CylinderGeometry(Math.max(b.r1 ?? 0, 0.01), b.r0, b.len, b.seg ?? 10).toNonIndexed();
      g.rotateZ(-Math.PI / 2);
    } else {
      g = new THREE.BoxGeometry(b.w, b.h, b.d).toNonIndexed();
    }
    g.translate(b.x, b.y, b.z);
    const pos = g.getAttribute("position");
    const nor = g.getAttribute("normal");
    c.set(b.color);
    for (let i = 0; i < pos.count; i++) {
      positions.push(pos.getX(i), pos.getY(i), pos.getZ(i));
      normals.push(nor.getX(i), nor.getY(i), nor.getZ(i));
      colors.push(c.r, c.g, c.b);
    }
    g.dispose();
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  return geo;
}

const dark = 0x2b2f36;
const glass = 0x9fd8f0;
const glassDeep = 0x6fb6d8;
const gunmetal = 0x1c1e22;
const steel = 0x6a6f78;
const exhaustBrown = 0x4a3a2c;
const tire = 0x16171a;
const navRed = 0xff4444;
const navGreen = 0x35e06a;

/** Darken/lighten a hex color by factor k (panel shading, ailerons, bellies). */
function shade(hex, k) {
  const c = new THREE.Color(hex).multiplyScalar(k);
  return c.getHex();
}

/** Duplicate boxes across the z=0 plane (port/starboard symmetry). */
const mirrorZ = (boxes) => boxes.flatMap((b) => [b, { ...b, z: -b.z }]);

// --- class silhouettes ---------------------------------------------------
// Still chunky boxes only, but with tapered fuselages, framed canopies,
// engine details, roundels, control-surface accents, and nav lights so the
// planes read as machines rather than bricks.

function spitfireBoxes(t) {
  return [
    // Fuselage, tapering toward the tail, with a darker belly line.
    { x: 0.7, y: 0, z: 0, w: 4.6, h: 1.3, d: 1.4, color: t.body },
    { x: -2.2, y: 0.06, z: 0, w: 1.7, h: 1.08, d: 1.12, color: t.body },
    { x: -3.5, y: 0.12, z: 0, w: 1.3, h: 0.84, d: 0.88, color: t.body },
    { x: 0.7, y: -0.6, z: 0, w: 4.2, h: 0.28, d: 1.1, color: shade(t.body, 0.72) },
    // Radial cowl, cooling-flap ring, lip, and spinner.
    { x: 3.45, y: 0, z: 0, w: 1.5, h: 1.5, d: 1.6, color: dark },
    { x: 2.78, y: 0, z: 0, w: 0.18, h: 1.56, d: 1.66, color: gunmetal }, // cowl flaps
    { x: 4.25, y: 0, z: 0, w: 0.3, h: 1.3, d: 1.4, color: gunmetal },
    { x: 4.45, y: 0, z: 0, w: 0.55, h: 0.6, d: 0.6, color: steel },
    ...mirrorZ([
      { x: 2.65, y: -0.18, z: 0.78, w: 1.5, h: 0.28, d: 0.18, color: exhaustBrown }, // exhausts
      // Quad battery: two fifties a foot apart on each wing.
      { x: 2.05, y: -0.3, z: 2.95, w: 1.1, h: 0.22, d: 0.15, color: gunmetal }, // wing guns
      { x: 2.88, y: -0.3, z: 2.95, w: 0.55, h: 0.12, d: 0.12, color: steel }, // barrel tips
      { x: 2.05, y: -0.3, z: 3.25, w: 1.1, h: 0.22, d: 0.15, color: gunmetal },
      { x: 2.88, y: -0.3, z: 3.25, w: 0.55, h: 0.12, d: 0.12, color: steel },
    ]),
    { x: 1.3, y: -0.28, z: 4.6, w: 1.0, h: 0.07, d: 0.07, color: steel }, // pitot tube
    { x: 2.9, y: -0.72, z: 0, w: 1.2, h: 0.34, d: 0.7, color: dark }, // chin intake
    // Canopy: windscreen frame, glasshouse, rear frame, headrest fairing.
    { x: 1.55, y: 0.86, z: 0, w: 0.3, h: 0.55, d: 1.05, color: t.body },
    { x: 0.85, y: 0.95, z: 0, w: 1.3, h: 0.7, d: 0.95, color: glass },
    { x: 0.16, y: 0.84, z: 0, w: 0.24, h: 0.5, d: 1.0, color: t.body },
    { x: -0.75, y: 0.7, z: 0, w: 1.6, h: 0.46, d: 0.78, color: t.body },
    { x: 0.85, y: 1.32, z: 0, w: 1.34, h: 0.1, d: 0.5, color: glassDeep }, // canopy crown
    { x: 1.0, y: 1.45, z: 0, w: 0.1, h: 0.45, d: 0.1, color: dark }, // antenna mast
    // Turtledeck spine tapering from the headrest down to the tail.
    { x: -2.25, y: 0.66, z: 0, w: 1.5, h: 0.22, d: 0.62, color: t.body },
    { x: -3.45, y: 0.56, z: 0, w: 1.1, h: 0.18, d: 0.5, color: t.body },
    // Wings: three stacked slabs of shrinking thickness fake a real taper.
    { x: 0.5, y: -0.42, z: 0, w: 2.7, h: 0.4, d: 7.6, color: t.color },
    { x: 0.46, y: -0.4, z: 0, w: 2.45, h: 0.33, d: 9.4, color: t.color },
    { x: 0.42, y: -0.36, z: 0, w: 2.2, h: 0.3, d: 11.0, color: t.color },
    ...mirrorZ([
      { x: 0.4, y: -0.34, z: 5.3, w: 1.5, h: 0.36, d: 0.6, color: t.accent }, // tip caps
      { x: 0.7, y: -0.14, z: 3.3, w: 1.5, h: 0.1, d: 1.5, color: t.accent }, // roundel
      { x: 0.7, y: -0.06, z: 3.3, w: 0.9, h: 0.1, d: 0.9, color: t.color },
      { x: -0.55, y: -0.34, z: 3.9, w: 0.5, h: 0.26, d: 2.4, color: shade(t.color, 0.78) }, // aileron
    ]),
    { x: 0.4, y: -0.26, z: 5.66, w: 0.26, h: 0.26, d: 0.26, color: navGreen },
    { x: 0.4, y: -0.26, z: -5.66, w: 0.26, h: 0.26, d: 0.26, color: navRed },
    // Tailplane with elevator strip and tip caps, fin fillet, fin + rudder.
    { x: -4.0, y: 0.35, z: 0, w: 1.3, h: 0.24, d: 4.4, color: t.color },
    { x: -4.62, y: 0.35, z: 0, w: 0.34, h: 0.2, d: 4.0, color: shade(t.color, 0.78) },
    ...mirrorZ([{ x: -4.05, y: 0.35, z: 2.0, w: 1.1, h: 0.26, d: 0.42, color: t.accent }]),
    { x: -3.3, y: 0.58, z: 0, w: 1.3, h: 0.3, d: 0.22, color: t.body }, // fin fillet
    { x: -3.95, y: 1.1, z: 0, w: 1.0, h: 1.5, d: 0.26, color: t.body },
    { x: -4.5, y: 1.2, z: 0, w: 0.5, h: 1.55, d: 0.24, color: t.color },
    { x: -4.5, y: 1.0, z: 0, w: 0.46, h: 0.34, d: 0.3, color: t.accent }, // rudder stripe
    { x: -4.05, y: 1.92, z: 0, w: 0.7, h: 0.26, d: 0.3, color: t.accent }, // fin tip
    { x: -3.9, y: -0.5, z: 0, w: 0.28, h: 0.46, d: 0.2, color: tire }, // tailwheel
  ];
}

// Long-nosed, racy, with a bubble canopy set far back and clipped wings.
function mustangBoxes(t) {
  return [
    // Long inline-engine fuselage with belly shading and rear taper.
    { x: 1.0, y: 0, z: 0, w: 6.2, h: 1.25, d: 1.3, color: t.body },
    { x: -2.9, y: 0.07, z: 0, w: 1.9, h: 1.0, d: 1.05, color: t.body },
    { x: -4.2, y: 0.14, z: 0, w: 1.0, h: 0.78, d: 0.84, color: t.body },
    { x: 1.0, y: -0.56, z: 0, w: 5.6, h: 0.26, d: 1.0, color: shade(t.body, 0.72) },
    // Pointed cowl with flap ring, lip, spinner; stacked nose exhausts.
    { x: 4.45, y: -0.02, z: 0, w: 1.4, h: 1.4, d: 1.45, color: dark },
    { x: 3.85, y: -0.02, z: 0, w: 0.2, h: 1.52, d: 1.56, color: gunmetal }, // cowl flaps
    { x: 5.15, y: 0, z: 0, w: 0.3, h: 1.15, d: 1.2, color: gunmetal },
    { x: 5.4, y: 0, z: 0, w: 0.6, h: 0.65, d: 0.65, color: steel },
    ...mirrorZ([
      { x: 3.55, y: 0.34, z: 0.62, w: 1.7, h: 0.2, d: 0.16, color: exhaustBrown },
      // The 20 mm pair: one hispano on each wing — the cannon doctrine
      // stops at two barrels, the punch is in the shell.
      { x: 3.3, y: -0.25, z: 3.2, w: 1.2, h: 0.26, d: 0.18, color: gunmetal }, // wing guns
      { x: 4.15, y: -0.25, z: 3.2, w: 0.6, h: 0.13, d: 0.13, color: steel }, // barrel tips
    ]),
    { x: 1.1, y: -0.26, z: 4.8, w: 1.0, h: 0.07, d: 0.07, color: steel }, // pitot tube
    { x: 4.0, y: -0.76, z: 0, w: 1.4, h: 0.38, d: 0.8, color: dark }, // chin scoop
    { x: -0.5, y: -0.82, z: 0, w: 2.2, h: 0.46, d: 0.9, color: dark }, // belly radiator
    { x: 0.65, y: -0.8, z: 0, w: 0.3, h: 0.4, d: 0.8, color: gunmetal },
    // Bubble canopy: framed windscreen, two stacked glass tiers.
    { x: 0.62, y: 0.78, z: 0, w: 0.26, h: 0.5, d: 1.0, color: t.body },
    { x: -0.35, y: 0.85, z: 0, w: 1.7, h: 0.55, d: 1.0, color: glass },
    { x: -0.45, y: 1.22, z: 0, w: 1.15, h: 0.34, d: 0.78, color: glassDeep },
    // Racing stripe down the spine; turtledeck fairing aft of the canopy.
    { x: 1.6, y: 0.66, z: 0, w: 5.6, h: 0.12, d: 0.5, color: t.accent },
    { x: -1.9, y: 0.62, z: 0, w: 1.7, h: 0.22, d: 0.6, color: t.body },
    { x: -3.3, y: 0.54, z: 0, w: 1.2, h: 0.18, d: 0.48, color: t.body },
    // Clipped wings (three taper slabs), roundels, ailerons, tip lights.
    { x: 0.8, y: -0.36, z: 0, w: 2.3, h: 0.34, d: 8.4, color: t.color },
    { x: 0.62, y: -0.34, z: 0, w: 1.95, h: 0.3, d: 9.9, color: t.color },
    { x: 0.5, y: -0.32, z: 0, w: 1.6, h: 0.26, d: 11.4, color: t.color },
    ...mirrorZ([
      { x: 0.5, y: -0.3, z: 5.5, w: 1.2, h: 0.3, d: 0.5, color: t.accent },
      { x: 0.8, y: -0.12, z: 3.5, w: 1.4, h: 0.1, d: 1.4, color: t.accent },
      { x: 0.8, y: -0.04, z: 3.5, w: 0.85, h: 0.1, d: 0.85, color: t.color },
      { x: -0.42, y: -0.3, z: 4.2, w: 0.46, h: 0.22, d: 2.3, color: shade(t.color, 0.78) },
    ]),
    { x: 0.5, y: -0.22, z: 5.8, w: 0.26, h: 0.26, d: 0.26, color: navGreen },
    { x: 0.5, y: -0.22, z: -5.8, w: 0.26, h: 0.26, d: 0.26, color: navRed },
    // Tail group with fin fillet and elevator tip caps.
    { x: -4.5, y: 0.32, z: 0, w: 1.2, h: 0.22, d: 3.9, color: t.color },
    { x: -5.05, y: 0.32, z: 0, w: 0.3, h: 0.18, d: 3.5, color: shade(t.color, 0.78) },
    ...mirrorZ([{ x: -4.55, y: 0.32, z: 1.78, w: 1.0, h: 0.24, d: 0.38, color: t.accent }]),
    { x: -3.7, y: 0.55, z: 0, w: 1.3, h: 0.28, d: 0.2, color: t.body }, // fin fillet
    { x: -4.3, y: 0.95, z: 0, w: 0.9, h: 1.3, d: 0.24, color: t.body },
    { x: -4.8, y: 1.05, z: 0, w: 0.46, h: 1.45, d: 0.22, color: t.color },
    { x: -4.8, y: 0.85, z: 0, w: 0.42, h: 0.3, d: 0.28, color: t.accent },
    { x: -4.35, y: 1.66, z: 0, w: 0.6, h: 0.22, d: 0.28, color: t.accent },
    { x: -4.1, y: -0.42, z: 0, w: 0.28, h: 0.44, d: 0.2, color: tire },
  ];
}

// Twin-engine slab with wing nacelles and a glass nose. Reads HEAVY.
function lightningBoxes(t) {
  return [
    // Slab fuselage with shaded belly and bomb-bay doors.
    { x: 0.2, y: 0, z: 0, w: 7.2, h: 1.7, d: 1.8, color: t.body },
    { x: 0.1, y: -0.92, z: 0, w: 6.2, h: 0.3, d: 1.5, color: shade(t.body, 0.72) },
    { x: 0.4, y: -1.04, z: 0, w: 2.6, h: 0.18, d: 1.1, color: gunmetal },
    // Framed bombardier glass nose + chin cannons.
    { x: 3.95, y: 0.1, z: 0, w: 1.1, h: 1.25, d: 1.35, color: glass },
    { x: 3.95, y: 0.12, z: 0, w: 1.2, h: 0.16, d: 1.45, color: t.body },
    { x: 4.55, y: 0.02, z: 0, w: 0.3, h: 0.9, d: 1.0, color: glassDeep },
    // The single 30 mm cannon on the centerline, slung in a chin fairing
    // under the bombardier's glass (a twin has no central spinner to fire
    // through) — one heavy barrel is the whole battery, streaming at the
    // full per-round rate: the jackpot every gunner hunts, dealt one card
    // at a time.
    { x: 4.3, y: -0.62, z: 0, w: 1.3, h: 0.32, d: 0.32, color: gunmetal },
    { x: 5.2, y: -0.62, z: 0, w: 0.75, h: 0.18, d: 0.18, color: steel }, // barrel
    // Stepped cockpit: windscreen frame, glasshouse, side panes, roof rail.
    { x: 1.75, y: 1.0, z: 0, w: 0.3, h: 0.66, d: 1.3, color: t.body },
    { x: 0.9, y: 1.1, z: 0, w: 1.7, h: 0.8, d: 1.2, color: glass },
    ...mirrorZ([{ x: 0.9, y: 1.05, z: 0.64, w: 1.5, h: 0.5, d: 0.1, color: glassDeep }]),
    { x: 0.9, y: 1.54, z: 0, w: 1.8, h: 0.1, d: 1.28, color: t.body },
    { x: 1.7, y: 1.75, z: 0, w: 0.1, h: 0.5, d: 0.1, color: dark },
    // Dorsal turret: steel ring, glass dome, twin guns.
    { x: -1.5, y: 0.98, z: 0, w: 1.1, h: 0.34, d: 1.1, color: steel },
    { x: -1.5, y: 1.26, z: 0, w: 0.85, h: 0.44, d: 0.85, color: glassDeep },
    ...mirrorZ([{ x: -0.85, y: 1.3, z: 0.18, w: 1.1, h: 0.12, d: 0.12, color: gunmetal }]),
    // Broad three-slab tapered wing, roundels, ailerons, tip caps + lights.
    { x: 0.2, y: -0.52, z: 0, w: 3.1, h: 0.42, d: 9.6, color: t.color },
    { x: 0.15, y: -0.49, z: 0, w: 2.75, h: 0.37, d: 12.0, color: t.color },
    { x: 0.1, y: -0.46, z: 0, w: 2.4, h: 0.32, d: 14.2, color: t.color },
    { x: 1.1, y: -0.34, z: 6.0, w: 1.0, h: 0.07, d: 0.07, color: steel }, // pitot tube
    ...mirrorZ([
      { x: 0.5, y: -0.24, z: 5.2, w: 1.6, h: 0.1, d: 1.6, color: t.accent },
      { x: 0.5, y: -0.16, z: 5.2, w: 1.0, h: 0.1, d: 1.0, color: t.color },
      { x: -1.25, y: -0.44, z: 5.7, w: 0.5, h: 0.24, d: 2.6, color: shade(t.color, 0.78) },
      { x: 0.1, y: -0.42, z: 6.9, w: 1.3, h: 0.38, d: 0.6, color: t.accent },
      // Nacelles: cowl, flap ring, lip, spinner hub, tail cone, exhaust.
      { x: 1.2, y: -0.34, z: 3.6, w: 2.8, h: 1.25, d: 1.25, color: dark },
      { x: 1.95, y: -0.34, z: 3.6, w: 0.22, h: 1.39, d: 1.39, color: gunmetal },
      { x: 2.65, y: -0.32, z: 3.6, w: 0.3, h: 1.05, d: 1.05, color: gunmetal },
      { x: 2.88, y: -0.32, z: 3.6, w: 0.5, h: 0.55, d: 0.55, color: steel },
      { x: -0.45, y: -0.4, z: 3.6, w: 1.0, h: 0.9, d: 0.95, color: shade(0x2b2f36, 1.3) },
      { x: 0.9, y: -0.18, z: 4.28, w: 1.2, h: 0.2, d: 0.16, color: exhaustBrown },
    ]),
    { x: 0.1, y: -0.34, z: 7.25, w: 0.28, h: 0.28, d: 0.28, color: navGreen },
    { x: 0.1, y: -0.34, z: -7.25, w: 0.28, h: 0.28, d: 0.28, color: navRed },
    // Tail: shoulder fairing, tapered boom + cone, broad tailplane, twin fins.
    { x: -3.1, y: 0.55, z: 0, w: 1.6, h: 0.5, d: 1.45, color: t.body },
    { x: -3.7, y: 0.22, z: 0, w: 2.2, h: 1.15, d: 1.25, color: t.body },
    { x: -4.95, y: 0.24, z: 0, w: 0.7, h: 0.85, d: 0.95, color: shade(t.body, 0.9) },
    { x: -4.55, y: 0.45, z: 0, w: 1.4, h: 0.28, d: 5.8, color: t.color },
    { x: -5.18, y: 0.45, z: 0, w: 0.36, h: 0.22, d: 5.2, color: shade(t.color, 0.78) },
    ...mirrorZ([
      { x: -4.4, y: 1.25, z: 2.7, w: 1.0, h: 1.55, d: 0.3, color: t.color },
      { x: -4.85, y: 1.25, z: 2.7, w: 0.36, h: 1.3, d: 0.34, color: t.accent },
    ]),
    { x: -3.6, y: -0.6, z: 0, w: 0.3, h: 0.5, d: 0.22, color: tire },
  ];
}

// Four-engine heavy bomber: long slab fuselage, glass bombardier nose,
// huge straight wing with four nacelles, tall single fin, tail-gun glass.
function fortressBoxes(t) {
  return [
    // Long fuselage with shaded belly and open-looking bomb-bay strake.
    { x: 0.0, y: 0, z: 0, w: 10.4, h: 2.0, d: 2.1, color: t.body },
    { x: -6.0, y: 0.15, z: 0, w: 2.4, h: 1.5, d: 1.6, color: t.body },
    { x: -0.4, y: -1.08, z: 0, w: 8.6, h: 0.34, d: 1.7, color: shade(t.body, 0.72) },
    { x: 0.6, y: -1.24, z: 0, w: 4.2, h: 0.2, d: 1.3, color: gunmetal }, // bomb-bay doors
    // Framed bombardier glass nose.
    { x: 5.5, y: 0.12, z: 0, w: 1.3, h: 1.5, d: 1.6, color: glass },
    { x: 5.5, y: 0.14, z: 0, w: 1.4, h: 0.18, d: 1.7, color: t.body },
    { x: 6.2, y: 0.02, z: 0, w: 0.34, h: 1.05, d: 1.15, color: glassDeep },
    ...mirrorZ([
      { x: 5.9, y: -0.3, z: 0.9, w: 1.0, h: 0.2, d: 0.2, color: gunmetal }, // chin guns
      { x: 6.6, y: -0.3, z: 0.9, w: 0.5, h: 0.14, d: 0.14, color: steel }, // barrel tips
      { x: 4.0, y: 0.5, z: 1.0, w: 1.3, h: 0.4, d: 0.14, color: glassDeep }, // nose side panes
    ]),
    // Stepped flight deck high on the nose; spine fairing running aft.
    { x: 3.4, y: 1.22, z: 0, w: 0.34, h: 0.7, d: 1.5, color: t.body },
    { x: 2.45, y: 1.3, z: 0, w: 1.8, h: 0.85, d: 1.4, color: glass },
    { x: 2.45, y: 1.78, z: 0, w: 1.9, h: 0.12, d: 1.48, color: t.body },
    { x: 3.2, y: 2.0, z: 0, w: 0.1, h: 0.55, d: 0.1, color: dark }, // antenna
    { x: 0.6, y: 1.06, z: 0, w: 2.4, h: 0.18, d: 1.1, color: t.body },
    { x: -4.3, y: 0.95, z: 0, w: 2.0, h: 0.16, d: 0.85, color: t.body },
    // Dorsal turret ring amidships (the rotating dome + guns are a separate
    // articulated mount — see CLASS_TURRETS / PlaneVisual.setTurretAim).
    { x: -1.6, y: 1.18, z: 0, w: 1.2, h: 0.36, d: 1.2, color: steel },
    // Tail-gunner glasshouse and waist-gun blisters (the twin tail stingers
    // are articulated too).
    { x: -7.35, y: 0.3, z: 0, w: 0.9, h: 1.0, d: 1.1, color: glassDeep },
    ...mirrorZ([
      { x: -3.4, y: 0.05, z: 1.04, w: 0.9, h: 0.6, d: 0.16, color: glassDeep },
    ]),
    // Huge straight wing: three stacked slabs of shrinking thickness.
    { x: 0.6, y: -0.5, z: 0, w: 3.6, h: 0.5, d: 12.5, color: t.color },
    { x: 0.5, y: -0.46, z: 0, w: 3.25, h: 0.43, d: 16.2, color: t.color },
    { x: 0.4, y: -0.42, z: 0, w: 2.9, h: 0.36, d: 19.6, color: t.color },
    { x: 1.9, y: -0.34, z: 8.6, w: 1.1, h: 0.08, d: 0.08, color: steel }, // pitot tube
    ...mirrorZ([
      { x: 0.8, y: -0.2, z: 7.0, w: 1.9, h: 0.1, d: 1.9, color: t.accent }, // roundel
      { x: 0.8, y: -0.12, z: 7.0, w: 1.15, h: 0.1, d: 1.15, color: t.color },
      { x: -1.2, y: -0.4, z: 8.2, w: 0.55, h: 0.26, d: 3.0, color: shade(t.color, 0.78) }, // aileron
      { x: 0.4, y: -0.38, z: 9.5, w: 1.5, h: 0.42, d: 0.7, color: t.accent }, // tip caps
      // Inboard + outboard nacelles: cowl, flap ring, lip, hub, tail cone.
      { x: 1.7, y: -0.4, z: 2.9, w: 3.0, h: 1.35, d: 1.35, color: dark },
      { x: 2.45, y: -0.4, z: 2.9, w: 0.22, h: 1.49, d: 1.49, color: gunmetal },
      { x: 3.25, y: -0.38, z: 2.9, w: 0.3, h: 1.1, d: 1.1, color: gunmetal },
      { x: 3.5, y: -0.38, z: 2.9, w: 0.5, h: 0.6, d: 0.6, color: steel },
      { x: 0.0, y: -0.46, z: 2.9, w: 1.0, h: 0.95, d: 1.0, color: shade(0x2b2f36, 1.3) },
      { x: 1.5, y: -0.24, z: 3.6, w: 1.2, h: 0.2, d: 0.16, color: exhaustBrown },
      { x: 1.3, y: -0.4, z: 5.7, w: 2.7, h: 1.2, d: 1.2, color: dark },
      { x: 1.95, y: -0.4, z: 5.7, w: 0.22, h: 1.34, d: 1.34, color: gunmetal },
      { x: 2.7, y: -0.38, z: 5.7, w: 0.3, h: 1.0, d: 1.0, color: gunmetal },
      { x: 2.93, y: -0.38, z: 5.7, w: 0.5, h: 0.55, d: 0.55, color: steel },
      { x: -0.2, y: -0.46, z: 5.7, w: 0.9, h: 0.85, d: 0.9, color: shade(0x2b2f36, 1.3) },
    ]),
    { x: 0.4, y: -0.28, z: 10.0, w: 0.3, h: 0.3, d: 0.3, color: navGreen },
    { x: 0.4, y: -0.28, z: -10.0, w: 0.3, h: 0.3, d: 0.3, color: navRed },
    // Tail: broad tailplane with tip caps, towering single fin + fillet.
    { x: -6.3, y: 0.55, z: 0, w: 1.7, h: 0.3, d: 7.4, color: t.color },
    { x: -7.05, y: 0.55, z: 0, w: 0.4, h: 0.24, d: 6.6, color: shade(t.color, 0.78) },
    ...mirrorZ([{ x: -6.35, y: 0.55, z: 3.42, w: 1.3, h: 0.28, d: 0.5, color: t.accent }]),
    { x: -5.0, y: 0.95, z: 0, w: 1.6, h: 0.35, d: 0.3, color: t.body }, // fin fillet
    { x: -6.0, y: 1.7, z: 0, w: 1.5, h: 2.4, d: 0.34, color: t.body },
    { x: -6.9, y: 1.85, z: 0, w: 0.6, h: 2.5, d: 0.3, color: t.color },
    { x: -6.9, y: 1.35, z: 0, w: 0.55, h: 0.45, d: 0.38, color: t.accent }, // rudder stripe
    { x: -6.1, y: 2.95, z: 0, w: 1.0, h: 0.3, d: 0.4, color: t.accent }, // fin tip
    { x: -5.6, y: -0.7, z: 0, w: 0.34, h: 0.55, d: 0.26, color: tire }, // tailwheel
  ];
}

// TBF Avenger torpedo bomber: a stout single-engine machine with a long
// greenhouse canopy, cranked gull wings, and the fish slung under the belly.
function avengerBoxes(t) {
  return [
    // Deep fuselage with a shaded belly and tapered tail.
    { x: 0.4, y: 0, z: 0, w: 6.4, h: 1.7, d: 1.7, color: t.body },
    { x: -3.6, y: 0.1, z: 0, w: 2.2, h: 1.2, d: 1.3, color: t.body },
    { x: 0.4, y: -0.85, z: 0, w: 6.0, h: 0.3, d: 1.4, color: shade(t.body, 0.72) },
    // Radial cowl, flap ring, spinner.
    { x: 3.9, y: 0, z: 0, w: 1.6, h: 1.8, d: 1.9, color: dark },
    { x: 3.15, y: 0, z: 0, w: 0.2, h: 1.86, d: 1.96, color: gunmetal },
    { x: 4.75, y: 0, z: 0, w: 0.5, h: 0.7, d: 0.7, color: steel },
    ...mirrorZ([{ x: 3.4, y: -0.5, z: 0.7, w: 1.2, h: 0.24, d: 0.18, color: exhaustBrown }]),
    // Long greenhouse canopy for pilot + torpedo officer.
    { x: 1.0, y: 1.0, z: 0, w: 3.4, h: 0.7, d: 1.2, color: glass },
    { x: 1.0, y: 1.36, z: 0, w: 3.5, h: 0.12, d: 1.26, color: t.body },
    { x: 2.0, y: 1.0, z: 0, w: 0.2, h: 0.74, d: 1.26, color: t.body }, // frame
    { x: 0.0, y: 1.0, z: 0, w: 0.2, h: 0.74, d: 1.26, color: t.body },
    // The fish, slung on belly crutches.
    { x: 0.6, y: -1.25, z: 0, w: 4.6, h: 0.62, d: 0.62, color: 0x23262c },
    { x: 2.9, y: -1.25, z: 0, w: 0.5, h: 0.5, d: 0.5, color: steel }, // nose cap
    { x: -1.8, y: -1.22, z: 0, w: 0.5, h: 0.7, d: 0.7, color: gunmetal }, // screws
    { x: 1.6, y: -0.95, z: 0, w: 0.3, h: 0.3, d: 0.74, color: gunmetal }, // crutch
    { x: -0.6, y: -0.95, z: 0, w: 0.3, h: 0.3, d: 0.74, color: gunmetal },
    // Cranked gull wing: inner anhedral slab, outer span, tip caps.
    { x: 0.6, y: -0.6, z: 0, w: 2.9, h: 0.4, d: 5.4, color: t.color },
    { x: 0.5, y: -0.3, z: 0, w: 2.7, h: 0.34, d: 11.6, color: t.color },
    ...mirrorZ([
      // Quad battery: two fifties a foot apart on each outer wing panel.
      { x: 2.0, y: -0.42, z: 3.25, w: 1.0, h: 0.22, d: 0.15, color: gunmetal }, // wing guns
      { x: 2.7, y: -0.42, z: 3.25, w: 0.55, h: 0.12, d: 0.12, color: steel }, // barrel tips
      { x: 2.0, y: -0.42, z: 3.55, w: 1.0, h: 0.22, d: 0.15, color: gunmetal },
      { x: 2.7, y: -0.42, z: 3.55, w: 0.55, h: 0.12, d: 0.12, color: steel },
    ]),
    ...mirrorZ([
      { x: 0.6, y: -0.1, z: 4.4, w: 1.6, h: 0.1, d: 1.6, color: t.accent }, // roundel
      { x: 0.6, y: -0.04, z: 4.4, w: 0.95, h: 0.1, d: 0.95, color: t.color },
      { x: -0.7, y: -0.26, z: 5.0, w: 0.5, h: 0.22, d: 2.2, color: shade(t.color, 0.78) },
      { x: 0.4, y: -0.24, z: 5.6, w: 1.3, h: 0.38, d: 0.6, color: t.accent }, // tip caps
    ]),
    { x: 0.4, y: -0.16, z: 5.9, w: 0.26, h: 0.26, d: 0.26, color: navGreen },
    { x: 0.4, y: -0.16, z: -5.9, w: 0.26, h: 0.26, d: 0.26, color: navRed },
    // Tail: broad tailplane, tall rounded fin with rudder stripe.
    { x: -4.4, y: 0.5, z: 0, w: 1.5, h: 0.26, d: 4.6, color: t.color },
    ...mirrorZ([{ x: -4.45, y: 0.5, z: 2.1, w: 1.2, h: 0.24, d: 0.45, color: t.accent }]),
    { x: -4.2, y: 1.3, z: 0, w: 1.3, h: 1.7, d: 0.3, color: t.body },
    { x: -4.95, y: 1.4, z: 0, w: 0.5, h: 1.8, d: 0.26, color: t.color },
    { x: -4.95, y: 0.95, z: 0, w: 0.45, h: 0.4, d: 0.32, color: t.accent },
    { x: -4.3, y: 2.25, z: 0, w: 0.8, h: 0.26, d: 0.34, color: t.accent },
    { x: -4.2, y: -0.55, z: 0, w: 0.3, h: 0.5, d: 0.24, color: tire }, // tailwheel
  ];
}

// F-104 Starfighter: the early-jet interceptor — a needle-nosed
// pencil fuselage, side intakes with shock cones, tiny razor mid-wings with
// tip tanks, a T-tail, and rocket pods on the stub pylons. No propeller;
// PlaneVisual gives jet types an afterburner plume instead (CLASS_JETPIPES).
function starfighterBoxes(t) {
  return [
    // Pencil fuselage: long center tube tapering both ways, shaded belly.
    { x: 0.6, y: 0, z: 0, w: 7.6, h: 1.15, d: 1.15, color: t.body },
    { x: -3.9, y: 0.02, z: 0, w: 1.8, h: 1.0, d: 1.0, color: t.body },
    { x: 0.4, y: -0.52, z: 0, w: 7.0, h: 0.22, d: 0.95, color: shade(t.body, 0.72) },
    // Needle nose: a true smooth cone from the fuselage break to the tip,
    // with a short dark radome cap and the steel pitot boom off the point.
    { cone: true, x: 5.55, y: 0.02, z: 0, len: 2.5, r0: 0.57, r1: 0.16, seg: 12, color: t.body },
    { cone: true, x: 7.05, y: 0.05, z: 0, len: 0.55, r0: 0.16, r1: 0.02, seg: 12, color: dark },
    { x: 7.75, y: 0.06, z: 0, w: 0.9, h: 0.09, d: 0.09, color: steel }, // pitot boom
    // Vulcan cannon port on the nose's port cheek (the six-barrel gatling
    // lives inside; only the gas port and a soot streak show).
    { x: 4.35, y: -0.18, z: -0.5, w: 0.8, h: 0.22, d: 0.16, color: gunmetal },
    { x: 3.7, y: -0.18, z: -0.5, w: 0.6, h: 0.14, d: 0.12, color: 0x2a2620 },
    // Low-profile canopy faired into the spine, well forward.
    { x: 3.15, y: 0.62, z: 0, w: 0.24, h: 0.4, d: 0.8, color: t.body }, // windscreen frame
    { x: 2.45, y: 0.68, z: 0, w: 1.3, h: 0.5, d: 0.78, color: glass },
    { x: 2.45, y: 0.96, z: 0, w: 1.0, h: 0.16, d: 0.6, color: glassDeep },
    { x: 1.5, y: 0.6, z: 0, w: 0.7, h: 0.36, d: 0.74, color: t.body }, // headrest fairing
    { x: 0.0, y: 0.5, z: 0, w: 2.6, h: 0.22, d: 0.6, color: t.body }, // spine
    { x: -2.6, y: 0.44, z: 0, w: 2.6, h: 0.18, d: 0.5, color: t.body },
    ...mirrorZ([
      // Side intakes with half-cone centerbodies, flanking the cockpit.
      { x: 1.7, y: -0.05, z: 0.72, w: 1.7, h: 0.72, d: 0.5, color: shade(t.body, 0.85) },
      { x: 2.55, y: -0.05, z: 0.72, w: 0.3, h: 0.6, d: 0.4, color: dark }, // intake lip
      { x: 2.75, y: -0.05, z: 0.72, w: 0.5, h: 0.3, d: 0.2, color: steel }, // shock cone
      // Razor mid-wing: one thin slab each side, slight taper via a cap.
      { x: -0.7, y: -0.15, z: 1.95, w: 2.3, h: 0.16, d: 2.9, color: t.color },
      { x: -0.85, y: -0.15, z: 3.15, w: 1.8, h: 0.16, d: 0.9, color: t.color },
      { x: -1.55, y: -0.15, z: 2.2, w: 0.5, h: 0.14, d: 2.2, color: shade(t.color, 0.78) }, // aileron strip
      { x: -0.5, y: -0.05, z: 2.1, w: 1.1, h: 0.1, d: 1.1, color: t.accent }, // roundel
      // Tip tanks: smooth cigar pods capping the very end of the wing — a
      // cylinder body with cone nose and tail, centred on the tip edge.
      { cone: true, x: -0.5, y: -0.15, z: 3.62, len: 2.6, r0: 0.26, r1: 0.26, seg: 10, color: t.accent },
      { cone: true, x: 1.15, y: -0.15, z: 3.62, len: 0.7, r0: 0.26, r1: 0.03, seg: 10, color: steel },
      { cone: true, x: -2.15, y: -0.15, z: 3.62, len: 0.7, r0: 0.03, r1: 0.26, seg: 10, color: shade(t.accent, 0.8) },
      // Rocket pods slung under the inner wing: stubby seven-tube cans.
      { x: -0.4, y: -0.5, z: 1.5, w: 1.7, h: 0.44, d: 0.44, color: gunmetal },
      { x: 0.5, y: -0.5, z: 1.5, w: 0.24, h: 0.36, d: 0.36, color: 0x101114 }, // open tubes
    ]),
    { x: -0.5, y: 0.18, z: 3.62, w: 0.2, h: 0.14, d: 0.2, color: navGreen },
    { x: -0.5, y: 0.18, z: -3.62, w: 0.2, h: 0.14, d: 0.2, color: navRed },
    // Tall fin with a T-tail plane riding its top, plus a ventral strake.
    { x: -3.6, y: 0.6, z: 0, w: 1.3, h: 0.5, d: 0.22, color: t.body }, // fin fillet
    { x: -4.15, y: 1.5, z: 0, w: 1.1, h: 1.9, d: 0.24, color: t.body },
    { x: -4.7, y: 1.6, z: 0, w: 0.5, h: 1.9, d: 0.22, color: t.color }, // rudder
    { x: -4.7, y: 1.05, z: 0, w: 0.46, h: 0.34, d: 0.28, color: t.accent }, // rudder stripe
    { x: -4.3, y: 2.5, z: 0, w: 1.5, h: 0.18, d: 3.2, color: t.color }, // T-tailplane
    { x: -4.95, y: 2.5, z: 0, w: 0.34, h: 0.15, d: 2.9, color: shade(t.color, 0.78) },
    ...mirrorZ([{ x: -4.35, y: 2.5, z: 1.45, w: 1.2, h: 0.2, d: 0.34, color: t.accent }]),
    { x: -4.0, y: -0.62, z: 0, w: 1.0, h: 0.5, d: 0.16, color: t.body }, // ventral strake
    // Afterburner can: dark nozzle ring around the tailpipe.
    { x: -4.95, y: 0.02, z: 0, w: 0.6, h: 0.82, d: 0.82, color: gunmetal },
    { x: -5.25, y: 0.02, z: 0, w: 0.3, h: 0.62, d: 0.62, color: 0x101114 },
  ];
}

// --- the early-jets era hangar (docs/early-jets-era.md) --------------------
// Three more jet silhouettes for the era roster. Same rules as everything
// above: chunky boxes, nose +X, cones only where the real airplane was
// genuinely round. Era rosters name these via each type's `model` key and
// applyEraModels() swaps the per-index tables at boot.

// MiG-21: a stubby pencil with the intake IN THE NOSE around
// a shock cone, a big delta wing, a swept fin, and two missile rails. Reads
// instantly against the F-104's T-tail and tip tanks.
function mig21Boxes(t) {
  return [
    // Fuselage: shorter and a hair fatter than the F-104's pencil.
    { x: 0.2, y: 0, z: 0, w: 6.6, h: 1.2, d: 1.2, color: t.body },
    { x: -3.7, y: 0.04, z: 0, w: 1.6, h: 1.0, d: 1.0, color: t.body },
    { x: 0.2, y: -0.52, z: 0, w: 6.2, h: 0.22, d: 0.95, color: shade(t.body, 0.72) },
    // Nose intake: a ring lip with the steel shock cone poking through.
    { cone: true, x: 3.9, y: 0, z: 0, len: 1.4, r0: 0.6, r1: 0.5, seg: 12, color: t.body },
    { x: 4.68, y: 0, z: 0, w: 0.22, h: 1.0, d: 1.0, color: dark }, // intake lip
    { cone: true, x: 4.85, y: 0, z: 0, len: 1.0, r0: 0.34, r1: 0.04, seg: 10, color: steel }, // shock cone
    { x: 5.6, y: 0.3, z: 0, w: 0.8, h: 0.08, d: 0.08, color: steel }, // pitot boom, high like the real one
    // Canopy faired into a spine that runs all the way to the fin.
    { x: 2.6, y: 0.62, z: 0, w: 0.24, h: 0.38, d: 0.78, color: t.body },
    { x: 1.95, y: 0.68, z: 0, w: 1.2, h: 0.48, d: 0.76, color: glass },
    { x: 1.95, y: 0.95, z: 0, w: 0.9, h: 0.15, d: 0.58, color: glassDeep },
    { x: -0.3, y: 0.58, z: 0, w: 3.4, h: 0.3, d: 0.62, color: t.body }, // dorsal spine
    // The lone heavy cannon on the port cheek.
    { x: 3.0, y: -0.32, z: -0.5, w: 0.8, h: 0.2, d: 0.16, color: gunmetal },
    ...mirrorZ([
      // Delta wing: three stacked slabs whose chord shrinks outboard and
      // whose leading edge steps back — the triangle reads from every angle.
      { x: -0.9, y: -0.18, z: 1.05, w: 3.4, h: 0.16, d: 1.6, color: t.color },
      { x: -1.35, y: -0.18, z: 2.35, w: 2.4, h: 0.15, d: 1.2, color: t.color },
      { x: -1.8, y: -0.18, z: 3.3, w: 1.4, h: 0.14, d: 0.85, color: t.color },
      { x: -2.5, y: -0.18, z: 1.9, w: 0.45, h: 0.13, d: 1.7, color: shade(t.color, 0.78) }, // elevon strip
      { x: -0.7, y: -0.06, z: 1.6, w: 1.1, h: 0.1, d: 1.1, color: t.accent }, // roundel
      // Missile rail + slim round on each wing.
      { x: -0.4, y: -0.42, z: 2.5, w: 1.5, h: 0.14, d: 0.14, color: gunmetal },
      { cone: true, x: -0.2, y: -0.58, z: 2.5, len: 1.7, r0: 0.13, r1: 0.13, seg: 8, color: steel },
      { cone: true, x: 0.95, y: -0.58, z: 2.5, len: 0.5, r0: 0.13, r1: 0.02, seg: 8, color: t.accent },
    ]),
    { x: -1.8, y: -0.1, z: 3.75, w: 0.2, h: 0.14, d: 0.2, color: navGreen },
    { x: -1.8, y: -0.1, z: -3.75, w: 0.2, h: 0.14, d: 0.2, color: navRed },
    // Swept fin (mid-set tailplane, no T): the fillet fakes the sweep.
    { x: -3.3, y: 0.7, z: 0, w: 1.7, h: 0.5, d: 0.22, color: t.body },
    { x: -4.0, y: 1.4, z: 0, w: 1.2, h: 1.5, d: 0.22, color: t.body },
    { x: -4.55, y: 1.35, z: 0, w: 0.45, h: 1.5, d: 0.2, color: t.color }, // rudder
    { x: -4.5, y: 0.95, z: 0, w: 0.42, h: 0.3, d: 0.26, color: t.accent }, // rudder stripe
    { x: -4.2, y: 0.15, z: 0, w: 1.3, h: 0.16, d: 3.0, color: t.color }, // mid tailplane
    { x: -3.9, y: -0.6, z: 0, w: 0.9, h: 0.45, d: 0.14, color: t.body }, // ventral fin
    // Afterburner can.
    { x: -4.6, y: 0.02, z: 0, w: 0.55, h: 0.8, d: 0.8, color: gunmetal },
    { x: -4.88, y: 0.02, z: 0, w: 0.28, h: 0.6, d: 0.6, color: 0x101114 },
  ];
}

// F-4 Phantom II: a big, hunched twin-seat missile truck. Twin
// intakes, a low wing with UPTURNED tips, the anhedral (drooped) tailplane,
// four belly missiles and the strapped-on gun pod. Nothing else in the sky
// has this much airplane per airplane.
function phantomBoxes(t) {
  return [
    // Fuselage: broad and deep, with the hunched spine of the real thing.
    { x: 0.9, y: 0, z: 0, w: 7.4, h: 1.5, d: 1.7, color: t.body },
    { x: -3.6, y: 0.1, z: 0, w: 2.2, h: 1.2, d: 1.4, color: t.body },
    { x: 0.9, y: -0.7, z: 0, w: 7.0, h: 0.26, d: 1.4, color: shade(t.body, 0.72) },
    // Radome: blunter than the F-104's needle.
    { cone: true, x: 5.35, y: 0.05, z: 0, len: 2.1, r0: 0.72, r1: 0.14, seg: 12, color: t.body },
    { cone: true, x: 6.55, y: 0.08, z: 0, len: 0.5, r0: 0.14, r1: 0.03, seg: 10, color: dark },
    // Tandem cockpit: two glass segments under one long framed canopy.
    { x: 3.4, y: 0.88, z: 0, w: 0.26, h: 0.44, d: 0.9, color: t.body },
    { x: 2.7, y: 0.95, z: 0, w: 1.2, h: 0.55, d: 0.88, color: glass },
    { x: 1.9, y: 0.9, z: 0, w: 0.28, h: 0.44, d: 0.9, color: t.body }, // center frame
    { x: 1.2, y: 0.95, z: 0, w: 1.2, h: 0.55, d: 0.88, color: glass },
    { x: 0.2, y: 0.8, z: 0, w: 0.9, h: 0.4, d: 0.84, color: t.body }, // aft fairing
    { x: -1.4, y: 0.68, z: 0, w: 2.4, h: 0.3, d: 0.9, color: t.body }, // spine hump
    ...mirrorZ([
      // Big cheek intakes with splitter plates.
      { x: 2.3, y: 0.05, z: 1.05, w: 2.2, h: 1.1, d: 0.55, color: shade(t.body, 0.85) },
      { x: 3.35, y: 0.05, z: 1.05, w: 0.3, h: 0.95, d: 0.45, color: dark },
      { x: 3.35, y: 0.05, z: 0.78, w: 0.5, h: 1.0, d: 0.1, color: steel }, // splitter
      // Low wing with the famous upturned outer panel.
      { x: 0.3, y: -0.5, z: 1.9, w: 2.9, h: 0.2, d: 3.0, color: t.color },
      { x: 0.1, y: -0.28, z: 3.65, w: 2.2, h: 0.18, d: 0.85, color: t.color }, // upturned tip
      { x: -1.0, y: -0.5, z: 2.4, w: 0.5, h: 0.16, d: 2.0, color: shade(t.color, 0.78) }, // aileron
      { x: 0.55, y: -0.36, z: 2.2, w: 1.2, h: 0.1, d: 1.2, color: t.accent }, // roundel
      // Two semi-recessed missiles per side under the belly edge.
      { cone: true, x: 0.6, y: -0.86, z: 0.55, len: 2.0, r0: 0.15, r1: 0.15, seg: 8, color: steel },
      { cone: true, x: 1.95, y: -0.86, z: 0.55, len: 0.55, r0: 0.15, r1: 0.02, seg: 8, color: t.accent },
      { cone: true, x: -0.8, y: -0.92, z: 1.15, len: 2.0, r0: 0.15, r1: 0.15, seg: 8, color: steel },
      { cone: true, x: 0.55, y: -0.92, z: 1.15, len: 0.55, r0: 0.15, r1: 0.02, seg: 8, color: t.accent },
      // Twin engine trunks ending in side-by-side burner cans.
      { x: -4.55, y: -0.15, z: 0.45, w: 1.3, h: 0.85, d: 0.75, color: gunmetal },
      { x: -5.25, y: -0.15, z: 0.45, w: 0.3, h: 0.6, d: 0.55, color: 0x101114 },
      // Anhedral tailplane: drooped tips read as the Phantom.
      { x: -4.4, y: 0.25, z: 1.0, w: 1.5, h: 0.18, d: 1.4, color: t.color },
      { x: -4.5, y: -0.05, z: 2.1, w: 1.3, h: 0.16, d: 0.9, color: shade(t.color, 0.9) },
    ]),
    // The gun pod strapped on the centerline — the Vietnam lesson, visible.
    { cone: true, x: 3.3, y: -0.98, z: 0, len: 2.4, r0: 0.24, r1: 0.24, seg: 10, color: gunmetal },
    { cone: true, x: 4.75, y: -0.98, z: 0, len: 0.5, r0: 0.24, r1: 0.1, seg: 10, color: steel },
    { x: 0.3, y: 0.1, z: 3.95, w: 0.2, h: 0.14, d: 0.2, color: navGreen },
    { x: 0.3, y: 0.1, z: -3.95, w: 0.2, h: 0.14, d: 0.2, color: navRed },
    // One big broad fin.
    { x: -3.9, y: 0.9, z: 0, w: 1.8, h: 0.6, d: 0.24, color: t.body },
    { x: -4.5, y: 1.8, z: 0, w: 1.5, h: 1.7, d: 0.26, color: t.body },
    { x: -5.2, y: 1.75, z: 0, w: 0.5, h: 1.6, d: 0.22, color: t.color }, // rudder
    { x: -5.1, y: 1.2, z: 0, w: 0.46, h: 0.34, d: 0.3, color: t.accent },
  ];
}

// B-52: a rangefinder error of an airplane. A long tube, a
// tall fin, high shoulder-mounted wings that step BACK as they go out (the
// sweep), four twin-engine pods slung underneath, and the quad-.50 tail
// sting. It fills the sky the way the B-17 never did.
function stratofortressBoxes(t) {
  return [
    // Fuselage: a long pressure tube with a shaded belly.
    { x: -0.5, y: 0, z: 0, w: 17.0, h: 1.9, d: 1.9, color: t.body },
    { x: -0.5, y: -0.9, z: 0, w: 16.4, h: 0.3, d: 1.5, color: shade(t.body, 0.72) },
    { cone: true, x: 8.7, y: 0.1, z: 0, len: 2.6, r0: 0.95, r1: 0.3, seg: 12, color: t.body },
    { x: 10.1, y: 0.1, z: 0, w: 0.5, h: 0.55, d: 0.55, color: dark }, // nose cap
    // Flight-deck glazing: airliner-style strip windows, not a bomber glasshouse.
    { x: 7.2, y: 0.75, z: 0, w: 1.6, h: 0.5, d: 1.5, color: t.body },
    ...mirrorZ([{ x: 7.3, y: 0.8, z: 0.68, w: 1.2, h: 0.3, d: 0.12, color: glass }]),
    { x: 7.0, y: 1.0, z: 0, w: 0.9, h: 0.3, d: 1.1, color: glassDeep }, // eyebrow panes
    ...mirrorZ([
      // Shoulder wing in three swept steps (leading edge walks aft outboard).
      { x: 0.9, y: 0.75, z: 2.6, w: 3.6, h: 0.28, d: 4.4, color: t.color },
      { x: -0.4, y: 0.78, z: 6.6, w: 3.0, h: 0.24, d: 3.6, color: t.color },
      { x: -1.7, y: 0.8, z: 9.8, w: 2.4, h: 0.2, d: 2.8, color: t.color },
      { x: -2.9, y: 0.8, z: 7.0, w: 0.5, h: 0.18, d: 3.4, color: shade(t.color, 0.78) }, // flap line
      { x: 0.6, y: 0.95, z: 4.4, w: 1.3, h: 0.1, d: 1.3, color: t.accent }, // roundel
      { x: -2.6, y: 0.82, z: 11.1, w: 1.3, h: 0.22, d: 0.5, color: t.accent }, // tip cap
      // Four engine pods: two twin-pods per side slung on pylons.
      { x: 1.6, y: 0.2, z: 3.4, w: 0.24, h: 0.6, d: 0.2, color: t.body }, // pylon
      { cone: true, x: 1.9, y: -0.15, z: 3.25, len: 2.2, r0: 0.4, r1: 0.34, seg: 10, color: gunmetal },
      { cone: true, x: 1.9, y: -0.15, z: 3.62, len: 2.2, r0: 0.4, r1: 0.34, seg: 10, color: gunmetal },
      { x: 0.4, y: 0.25, z: 7.2, w: 0.24, h: 0.55, d: 0.2, color: t.body },
      { cone: true, x: 0.7, y: -0.1, z: 7.05, len: 2.1, r0: 0.38, r1: 0.32, seg: 10, color: gunmetal },
      { cone: true, x: 0.7, y: -0.1, z: 7.42, len: 2.1, r0: 0.38, r1: 0.32, seg: 10, color: gunmetal },
    ]),
    { x: -2.6, y: 0.95, z: 11.35, w: 0.24, h: 0.16, d: 0.24, color: navGreen },
    { x: -2.6, y: 0.95, z: -11.35, w: 0.24, h: 0.16, d: 0.24, color: navRed },
    // The tall broad fin and low-set tailplane.
    { x: -7.6, y: 1.4, z: 0, w: 2.2, h: 0.9, d: 0.26, color: t.body },
    { x: -8.3, y: 2.8, z: 0, w: 1.8, h: 2.4, d: 0.28, color: t.body },
    { x: -9.1, y: 2.7, z: 0, w: 0.55, h: 2.2, d: 0.24, color: t.color }, // rudder
    { x: -9.0, y: 1.7, z: 0, w: 0.5, h: 0.4, d: 0.32, color: t.accent },
    { x: -8.4, y: 0.3, z: 0, w: 2.0, h: 0.22, d: 7.6, color: t.color }, // tailplane
    ...mirrorZ([{ x: -8.5, y: 0.32, z: 3.6, w: 1.6, h: 0.26, d: 0.5, color: t.accent }]),
    // The tail gunner's quad-.50 station (the crewable mount rides on top).
    { x: -9.5, y: 0.5, z: 0, w: 1.0, h: 0.8, d: 1.0, color: t.body },
    { x: -9.9, y: 0.75, z: 0, w: 0.5, h: 0.35, d: 0.9, color: glassDeep },
  ];
}

// Per-model bundles for era rosters (applyEraModels): everything the visual
// tables say about one silhouette, keyed by the config's `model` name.
const ERA_MODELS = {
  starfang: {
    builder: starfighterBoxes,
    livery: { body: 0xa8adb2, color: 0x9ca1a6, accent: 0xe8e6dc }, // the F-104's bare metal
    props: [],
    jet: true,
    jetpipe: { pos: [-5.4, 0.02, 0] },
    trails: { nacelles: 0.9, tip: 4.6 },
    muzzles: [[4.8, -0.18, -0.5]],
    damage: null, // null = copy the WW2 index's anchors (set below)
    stripe: [{ x: -4.72, y: 1.62, z: 0, w: 0.52, h: 0.5, d: 0.26 }],
  },
  balalaika: {
    builder: mig21Boxes,
    livery: { body: 0xb0b4b8, color: 0xa4a8ac, accent: 0xe8e6dc }, // the MiG-21's bare metal
    props: [],
    jet: true,
    jetpipe: { pos: [-5.0, 0.02, 0] },
    trails: { nacelles: 0.8, tip: 3.8 },
    muzzles: [[3.5, -0.32, -0.5]],
    damage: {
      engine: { x: -4.2, y: 0.02, z: 0, r: 0.6 },
      wing: { x: -1.1, y: -0.18, tipZ: 3.8, chord: 1.4, thick: 0.2 },
      fuse: { x0: -3.0, x1: 3.2, y: 0.2, z: 0.6, h: 0.6 },
      canopy: { x: 1.95, y: 0.75, w: 1.2, d: 0.76 },
      tank: { x: 0.1, y: -0.56, len: 2.2 },
      tail: { x: -4.2, y: 0.15, spanZ: 1.5, finY: 1.6 },
    },
    stripe: [{ x: -4.5, y: 1.55, z: 0, w: 0.45, h: 0.5, d: 0.24 }],
  },
  rhino: {
    builder: phantomBoxes,
    livery: { body: 0x8a918a, color: 0x7a817a, accent: 0xe8e6dc }, // the Phantom's grey-green
    props: [],
    jet: true,
    jetpipe: { pos: [-5.3, -0.15, 0] },
    trails: { nacelles: 1.2, tip: 4.1 },
    muzzles: [[4.9, -0.98, 0]],
    damage: {
      engine: { x: -4.5, y: -0.15, z: 0.45, r: 0.7 },
      wing: { x: 0.2, y: -0.5, tipZ: 4.1, chord: 1.5, thick: 0.24 },
      fuse: { x0: -3.4, x1: 3.8, y: 0.35, z: 0.85, h: 0.9 },
      canopy: { x: 2.0, y: 1.0, w: 2.7, d: 0.9 },
      tank: { x: 0.6, y: -0.75, len: 3.0 },
      tail: { x: -4.6, y: 0.25, spanZ: 2.6, finY: 2.2 },
    },
    stripe: [{ x: -5.15, y: 1.8, z: 0, w: 0.5, h: 0.5, d: 0.28 }],
  },
  arclight: {
    builder: stratofortressBoxes,
    livery: { body: 0x9ea3a6, color: 0x8a8f92, accent: 0xe8e6dc }, // the B-52's bare metal
    props: [],
    jet: true, // eight turbojets — no plume, but the SOUND is all jet
    jetpipe: null, // podded engines, no burner plume — the contrails carry it
    trails: { nacelles: 3.4, outboard: 7.2, tip: 11.2 }, // one contrail per twin pod
    muzzles: [
      [10.3, -0.2, 0.3],
      [10.3, -0.2, -0.3],
    ],
    turrets: [{ kind: "tail", pos: [-9.6, 0.6, 0], stowYaw: Math.PI, stowPitch: 0.1, barrelLen: 1.6 }],
    damage: {
      engine: { x: 1.9, y: -0.15, z: 3.4, r: 0.75 },
      wing: { x: 0, y: 0.78, tipZ: 11.0, chord: 2.6, thick: 0.5 },
      fuse: { x0: -7.0, x1: 7.0, y: 0.7, z: 1.0, h: 1.4 },
      canopy: { x: 7.2, y: 0.95, w: 1.6, d: 1.5 },
      tank: { x: -0.5, y: -1.0, len: 5.0 },
      tail: { x: -8.4, y: 0.5, spanZ: 3.8, finY: 3.4 },
    },
    stripe: [{ x: -9.05, y: 2.9, z: 0, w: 0.6, h: 0.6, d: 0.3 }],
  },
};

/**
 * Rewire the per-index visual tables for an era roster (docs/early-jets-era.md).
 * The classic tables are indexed by the AIR game's aircraft order; an era
 * server serves a different roster, so each type names its silhouette via a
 * `model` key and this swaps every table in place (imports keep working) at
 * boot, before any geometry is built. A roster with no model keys (the WW2
 * game) is left completely alone.
 */
export function applyEraModels(aircraftTypes) {
  if (!aircraftTypes?.some((t) => t?.model)) return;
  aircraftTypes.forEach((t, i) => {
    const m = ERA_MODELS[t?.model];
    if (!m) return;
    CLASS_BUILDERS[i] = m.builder;
    CLASS_LIVERY[i] = m.livery;
    CLASS_PROPS[i] = m.props ?? [];
    if (m.jetpipe) CLASS_JETPIPES[i] = m.jetpipe;
    else delete CLASS_JETPIPES[i];
    if (m.jet) CLASS_JET.add(i);
    else CLASS_JET.delete(i);
    CLASS_TRAILS[i] = m.trails ?? CLASS_TRAILS[i];
    CLASS_MUZZLES[i] = m.muzzles ?? [];
    if (m.turrets) CLASS_TURRETS[i] = m.turrets;
    else delete CLASS_TURRETS[i];
    delete CLASS_BANNERS[i]; // no circus in an era roster
    if (m.damage) DAMAGE_ANCHORS[i] = m.damage;
    else if (i !== 5 && DAMAGE_ANCHORS[5]) DAMAGE_ANCHORS[i] = DAMAGE_ANCHORS[5]; // starfang keeps its own
    if (m.stripe) TAIL_STRIPE_ANCHORS[i] = m.stripe;
    if (ERA_MODEL[t.model]) CLASS_MODELS[i] = ERA_MODEL[t.model];
  });
  applyModelTrails();
}

/**
 * Wire the roster's variants and real batteries into the per-index tables,
 * at boot, before anything builds a plane (after applyEraModels).
 *
 * A variant (`variantOf`: the F4U-1C is a Corsair, the Mk XVI a Spitfire)
 * joins at the end of the roster and flies its parent's airframe: every
 * table indexed by class — the builder, the paint, the props, the trails,
 * the turrets, the damage and stripe anchors, the model, and so the office
 * — copies the parent's slot into its own. One whose outline differs names
 * its own model (`airframe`), re-cut from the parent's art.
 *
 * A real battery (`gun.mounts`, battery.js) names the tip of every barrel
 * the server fires from, measured off the model's own muzzles: the class
 * flashes exactly those — six fifties along a Mustang's wing, the 109's hub
 * and both cowl guns — and the model's muzzle list is not consulted.
 */
export function applyRoster(aircraftTypes, calByName) {
  const index = new Map((aircraftTypes ?? []).map((t, i) => [t?.name, i]));
  (aircraftTypes ?? []).forEach((t, i) => {
    const j = t?.variantOf ? index.get(t.variantOf) : undefined;
    if (j === undefined || j === i) return;
    CLASS_BUILDERS[i] = CLASS_BUILDERS[j];
    CLASS_LIVERY[i] = CLASS_LIVERY[j];
    CLASS_PROPS[i] = CLASS_PROPS[j];
    if (CLASS_JETPIPES[j]) CLASS_JETPIPES[i] = CLASS_JETPIPES[j];
    if (CLASS_JET.has(j)) CLASS_JET.add(i);
    if (CLASS_TRAILS[j]) CLASS_TRAILS[i] = CLASS_TRAILS[j];
    CLASS_MUZZLES[i] = CLASS_MUZZLES[j];
    if (CLASS_TURRETS[j]) CLASS_TURRETS[i] = CLASS_TURRETS[j];
    if (DAMAGE_ANCHORS[j]) DAMAGE_ANCHORS[i] = DAMAGE_ANCHORS[j];
    if (TAIL_STRIPE_ANCHORS[j]) TAIL_STRIPE_ANCHORS[i] = TAIL_STRIPE_ANCHORS[j];
    CLASS_MODELS[i] = CLASS_MODELS[j];
    // A variant whose outline isn't its parent's flies its own airframe
    // (`airframe`: the Mk XVI's clipped wing), and trails from its own tips;
    // its office and paint still come from the parent's family.
    if (t.airframe && AIRCRAFT_MODELS[t.airframe]) {
      CLASS_MODELS[i] = t.airframe;
      if (modelsEnabled() && AIRCRAFT_MODELS[t.airframe].trails) CLASS_TRAILS[i] = AIRCRAFT_MODELS[t.airframe].trails;
    }
  });
  (aircraftTypes ?? []).forEach((t, i) => {
    if (!t?.gun?.mounts?.length) return;
    CLASS_MUZZLES[i] = batteryMuzzles(t, calByName);
    EXACT_MUZZLES.add(i);
  });
}

// Classes whose flash list is the battery's own barrel tips (applyRoster):
// the model's muzzles aren't laid over them.
const EXACT_MUZZLES = new Set();

// --- the flying billboard circus (adfleet.go) ------------------------------
// Bot-only, teamless craft: they ignore the team palette and fly a carnival
// livery instead — cream, circus gold, and poster red — so nobody reads them
// as a bandit at a glance. Their banners are separate canvas-textured meshes
// built by PlaneVisual (see _buildBanner), not part of the merged hull.

const circusCream = 0xefe2c0;
const circusGold = 0xf0c04a;
const circusRed = 0xc8452e;
const zeppelinSkin = 0xc9cdd4;

// Skywriter: a fabric-and-wire barnstormer — twin stacked wings, struts,
// open cockpit, fixed gear with a tailskid, and a tow hook for the banner.
function skywriterBoxes() {
  return [
    // Fuselage, tapering aft, with a shaded belly line.
    { x: 0.7, y: 0, z: 0, w: 4.2, h: 1.2, d: 1.2, color: circusCream },
    { x: -1.9, y: 0.05, z: 0, w: 1.6, h: 1.0, d: 1.0, color: circusCream },
    { x: -3.1, y: 0.1, z: 0, w: 1.2, h: 0.8, d: 0.8, color: circusCream },
    { x: 0.7, y: -0.55, z: 0, w: 3.8, h: 0.24, d: 0.95, color: shade(circusCream, 0.74) },
    // Radial cowl in poster red, lip and spinner.
    { x: 3.1, y: 0, z: 0, w: 1.3, h: 1.4, d: 1.5, color: circusRed },
    { x: 3.8, y: 0, z: 0, w: 0.28, h: 1.2, d: 1.3, color: gunmetal },
    { x: 4.0, y: 0, z: 0, w: 0.5, h: 0.55, d: 0.55, color: steel },
    ...mirrorZ([{ x: 2.3, y: -0.16, z: 0.68, w: 1.3, h: 0.24, d: 0.16, color: exhaustBrown }]),
    // Open cockpit: a windscreen, a padded coaming, a headrest fairing.
    { x: 1.0, y: 0.78, z: 0, w: 0.24, h: 0.44, d: 0.9, color: glass },
    { x: 0.3, y: 0.68, z: 0, w: 1.2, h: 0.16, d: 1.0, color: shade(circusRed, 0.8) },
    { x: -0.9, y: 0.62, z: 0, w: 1.2, h: 0.34, d: 0.6, color: circusCream },
    // Twin stacked wings — the whole point of a biplane — gold with red tips.
    { x: 0.9, y: 1.2, z: 0, w: 2.2, h: 0.26, d: 11.0, color: circusGold }, // upper
    { x: 0.5, y: -0.5, z: 0, w: 2.1, h: 0.26, d: 9.2, color: circusGold }, // lower
    ...mirrorZ([
      { x: 0.9, y: 1.2, z: 5.25, w: 2.2, h: 0.3, d: 0.55, color: circusRed }, // upper tips
      { x: 0.5, y: -0.5, z: 4.35, w: 2.1, h: 0.3, d: 0.5, color: circusRed }, // lower tips
      // Interplane struts and flying wires do the "wire-and-fabric" read.
      { x: 1.35, y: 0.35, z: 3.4, w: 0.14, h: 1.75, d: 0.14, color: dark },
      { x: 0.35, y: 0.35, z: 3.4, w: 0.14, h: 1.75, d: 0.14, color: dark },
      // Cabane struts from fuselage crown to the upper wing.
      { x: 1.2, y: 0.95, z: 0.5, w: 0.12, h: 0.6, d: 0.12, color: dark },
      { x: 0.4, y: 0.95, z: 0.5, w: 0.12, h: 0.6, d: 0.12, color: dark },
      // Fixed landing gear: leg + fat tire.
      { x: 1.6, y: -1.0, z: 0.85, w: 0.16, h: 0.85, d: 0.16, color: dark },
      { x: 1.6, y: -1.5, z: 0.85, w: 0.66, h: 0.66, d: 0.3, color: tire },
    ]),
    { x: 1.6, y: -1.15, z: 0, w: 0.14, h: 0.14, d: 1.9, color: dark }, // axle
    { x: -3.6, y: -0.45, z: 0, w: 0.5, h: 0.5, d: 0.12, color: dark }, // tailskid
    // Tail: gold planes, a red fin, and the banner tow hook.
    { x: -3.5, y: 0.3, z: 0, w: 1.2, h: 0.2, d: 3.8, color: circusGold },
    ...mirrorZ([{ x: -3.5, y: 0.3, z: 1.8, w: 1.2, h: 0.24, d: 0.4, color: circusRed }]),
    { x: -3.6, y: 0.85, z: 0, w: 1.0, h: 1.1, d: 0.2, color: circusRed },
    { x: -4.15, y: 0.7, z: 0, w: 0.4, h: 1.3, d: 0.16, color: shade(circusRed, 0.8) }, // rudder
    { x: -3.9, y: 0.05, z: 0, w: 0.35, h: 0.18, d: 0.18, color: steel }, // tow hook
  ];
}

// Marquee: the ad zeppelin — a long silver gasbag with a passenger gondola
// slung beneath and cruciform tail fins. No propellers: it drifts on quiet
// station-keeping engines the eye doesn't need. The flank banners are
// separate textured meshes (PlaneVisual._buildBanner) sized to the hull.
function marqueeBoxes() {
  return [
    // Hull: a fat center section with smooth tapered nose and tail cones.
    { cone: true, x: 0, y: 0, z: 0, len: 12, r0: 3.1, r1: 3.1, seg: 14, color: zeppelinSkin },
    { cone: true, x: 9.4, y: 0, z: 0, len: 7, r0: 3.1, r1: 1.0, seg: 14, color: zeppelinSkin },
    { x: 13.1, y: 0, z: 0, w: 0.7, h: 1.1, d: 1.1, color: circusRed }, // nose cap
    { cone: true, x: -9.4, y: 0, z: 0, len: 7, r0: 1.1, r1: 3.1, seg: 14, color: zeppelinSkin },
    // Dorsal and ventral ridge lines so the roll reads.
    { x: 0, y: 3.05, z: 0, w: 11.5, h: 0.18, d: 0.5, color: shade(zeppelinSkin, 0.8) },
    { x: 0, y: -3.05, z: 0, w: 11.5, h: 0.18, d: 0.5, color: shade(zeppelinSkin, 0.8) },
    // Cruciform tail: fins and rudder in poster red.
    { x: -11.2, y: 1.9, z: 0, w: 3.4, h: 2.6, d: 0.24, color: circusRed },
    { x: -11.2, y: -1.9, z: 0, w: 3.4, h: 2.6, d: 0.24, color: circusRed },
    { x: -11.2, y: 0, z: 1.9, w: 3.4, h: 0.24, d: 2.6, color: circusRed },
    { x: -11.2, y: 0, z: -1.9, w: 3.4, h: 0.24, d: 2.6, color: circusRed },
    { x: -13.0, y: 0.9, z: 0, w: 0.6, h: 1.6, d: 0.2, color: shade(circusRed, 0.8) }, // rudder
    // Gondola: a warm-lit cabin slung under the bow third.
    { x: 2.6, y: -3.5, z: 0, w: 4.6, h: 1.3, d: 1.5, color: circusRed },
    { x: 4.4, y: -3.3, z: 0, w: 1.0, h: 0.6, d: 1.3, color: glass }, // wheelhouse glass
    ...mirrorZ([{ x: 2.2, y: -3.4, z: 0.78, w: 2.6, h: 0.45, d: 0.1, color: glassDeep }]), // cabin windows
    { x: 2.6, y: -4.3, z: 0, w: 2.0, h: 0.3, d: 0.5, color: dark }, // landing bumper
    // Mooring lines' anchor points, a nod to the ground crew.
    { x: 8.0, y: -1.9, z: 0, w: 0.25, h: 0.7, d: 0.25, color: dark },
  ];
}

export const CLASS_BUILDERS = [spitfireBoxes, mustangBoxes, lightningBoxes, fortressBoxes, avengerBoxes, starfighterBoxes, skywriterBoxes, marqueeBoxes];

// Each class's factory finish, the palette its builder paints with. Both teams
// fly it (aircraft_models.js has the paint decision), so it is lifted off the
// class's own model — past 2 km the box silhouette is the same airplane, in
// the same paint, the model was just inside it: `body` the fuselage, `color`
// the wings, tailplanes and rudder
// (the slot a team hue used to fill), `accent` the trim — tip caps, roundel
// rings, fin bands, in the model's `trim`. The circus builders paint their own
// carnival colors and ignore this.
const CLASS_LIVERY = [
  { body: 0x525954, color: 0x525954, accent: 0xc9d6a8 }, // 0 Spitfire: Dark Green and Ocean Grey mixed, as LOD1 paints them; Sky band
  { body: 0xb8bcc0, color: 0xaeb2b6, accent: 0x3858b8 }, // 1 Mustang: Pattie Ann's bare metal and blue nose
  { body: 0xb4b8bc, color: 0xaaaeb2, accent: 0xe2a634 }, // 2 P-38: Mama's Boy's bare metal and yellow spinners
  { body: 0x4b5130, color: 0x4b5130, accent: 0xe8e6dc }, // 3 B-17: olive drab, white group marks
  { body: 0x2e3a4f, color: 0x2e3a4f, accent: 0xd8dcdc }, // 4 Avenger: Navy sea blue and white
  { body: 0xa8adb2, color: 0x9ca1a6, accent: 0xe8e6dc }, // 5 F-104: bare metal, white tip tanks
];

// Ad-craft banner style per class (adfleet.go): "towed" hangs a cloth banner
// off the tail on a rope; "sides" paints one panel on each hull flank.
// Classes absent here fly no banner — this map is also how PlaneVisual knows
// a type belongs to the circus.
export const CLASS_BANNERS = {
  6: { kind: "towed" },
  7: { kind: "sides" },
};

// Per-class propeller spec: [x, z] positions of each prop.
const CLASS_PROPS = [
  [[4.5, 0]],
  [[5.4, 0]],
  [
    [2.95, 3.6],
    [2.95, -3.6],
  ],
  [
    [3.6, 2.9],
    [3.6, -2.9],
    [3.0, 5.7],
    [3.0, -5.7],
  ],
  [[5.0, 0]],
  [], // F-104: a jet — no propeller (see CLASS_JETPIPES)
  [[4.15, 0]], // Skywriter: one honest radial up front
  [], // Marquee: a zeppelin drifts — no visible prop, no jet
];

// Jet types: tailpipe position for the afterburner plume PlaneVisual builds
// in place of a propeller. Flame length/opacity ride the throttle.
export const CLASS_JETPIPES = {
  5: { pos: [-5.4, 0.02, 0] },
};

// Which types are turbojets — the audio discriminator (audio.js swaps the
// piston drone for a thrust roar + turbine whine). This is NOT the same set
// as CLASS_JETPIPES: the B-52's podded engines draw no burner plume but
// she's every bit a jet to the ear. Era rosters rewire this via each model's
// `jet` flag in applyEraModels.
export const CLASS_JET = new Set([5]);

// Contrail/streamer emit geometry per class (effects.js, roadmap #45): the
// body-frame ±Z offsets of the engine trails and the wingtip half-span the
// G-streamers snap off. A four-engine airframe adds its `outboard` pair's
// offset and writes four contrails. Data only — main.js rotates these into
// the world.
export const CLASS_TRAILS = {
  0: { nacelles: 1.8, tip: 5.2 }, // Spitfire
  1: { nacelles: 1.8, tip: 5.6 }, // Mustang
  2: { nacelles: 2.6, tip: 7.6 }, // P-38 (twin engines)
  3: { nacelles: 2.9, outboard: 5.7, tip: 10.4 }, // B-17 (four engines: four contrails)
  4: { nacelles: 1.8, tip: 6.4 }, // Avenger
  5: { nacelles: 0.9, tip: 4.6 }, // F-104 (jet: tight twin plume)
  6: { nacelles: 0.8, tip: 5.2 }, // Skywriter (the circus contrails too)
  7: { nacelles: 2.4, tip: 3.0 }, // Marquee (a lazy smudge off the gondola)
};

// Which art/ model flies each class index (aircraft_models.js). Era rosters
// rewire it in applyEraModels, like every other per-index table here.
export const CLASS_MODELS = [...CLASS_MODEL];

// With the models flying, contrails and G-streamers peel off the model's own
// wingtips and engines rather than the box's (a B-17's tips are 5 m further
// out than the B-17 box's ever were).
function applyModelTrails() {
  if (!modelsEnabled()) return;
  CLASS_MODELS.forEach((stem, i) => {
    const m = AIRCRAFT_MODELS[stem];
    if (m?.trails) CLASS_TRAILS[i] = m.trails;
  });
}
applyModelTrails();

// Articulated defensive turrets per class (the bombers and the Zerstörer):
// crewable mounts on the BOX silhouette's blister positions. The
// `turrets` entries in shared/config.json mirror the MODEL's turret pivots
// (aircraft_models.js: the B-17's tail gun and top turret) so server rounds
// leave where the model's barrels point; the box hull is shorter, so its
// turrets keep their own seats — they only ever show at 2 km or with ?boxes.
// Yaw is in the body frame (0 = over the nose, stowYaw parks the mount when
// unmanned).
export const CLASS_TURRETS = {
  3: [
    { kind: "tail", pos: [-7.8, 0.4, 0], stowYaw: Math.PI, stowPitch: 0.1, barrelLen: 1.5 },
    { kind: "top", pos: [-1.6, 1.7, 0], stowYaw: Math.PI, stowPitch: 0.12, barrelLen: 1.3 },
  ],
  // The hangar's later bombers have no box of their own (past 2 km they draw
  // the generic silhouette), so their seats sit at the model's own pivots —
  // the same numbers as their config `pos`. Each slot is also what carries a
  // model's turret pose and flashes (PlaneVisual.turrets), so one must exist
  // for every crewed seat.
  12: [{ kind: "tail", pos: [-1.2, 0.95, 0], stowYaw: Math.PI, stowPitch: 0.1, barrelLen: 1.0 }], // Bf 110 rear gun
  13: [
    { kind: "top", pos: [-1.0, 0.92, 0], stowYaw: Math.PI, stowPitch: 0.12, barrelLen: 1.3 }, // A-20 power turret
    { kind: "tail", pos: [-2.6, -0.82, 0], stowYaw: Math.PI, stowPitch: -0.1, barrelLen: 0.9 }, // tunnel gun
  ],
  14: [
    { kind: "tail", pos: [2.9, 1.22, 0], stowYaw: Math.PI, stowPitch: 0.1, barrelLen: 0.9 }, // Ju 88 B-stand
    { kind: "tail", pos: [1.75, -1.08, 0], stowYaw: Math.PI, stowPitch: -0.1, barrelLen: 0.7 }, // Bola
  ],
  15: [
    { kind: "tail", pos: [-1.25, 1.1, 0], stowYaw: Math.PI, stowPitch: 0.1, barrelLen: 1.0 }, // He 111 dorsal
    { kind: "tail", pos: [-1.4, -1.2, 0], stowYaw: Math.PI, stowPitch: -0.1, barrelLen: 0.8 }, // Bola
  ],
};

// Rotating part of a crew turret: the dorsal glass dome or the tail cradle,
// with a pitching twin-gun assembly nested inside (built by PlaneVisual).
function turretDomeBoxes(kind) {
  if (kind === "top") {
    return [
      { x: 0, y: -0.15, z: 0, w: 0.95, h: 0.5, d: 0.95, color: glassDeep },
      { x: 0.28, y: 0.08, z: 0, w: 0.5, h: 0.24, d: 0.7, color: steel }, // gun slot fairing
    ];
  }
  // Tail cradle: a small armored yoke under the glasshouse.
  return [{ x: 0.1, y: -0.12, z: 0, w: 0.5, h: 0.4, d: 0.6, color: steel }];
}

function turretGunBoxes(kind, barrelLen) {
  const y = kind === "top" ? 0.12 : 0.05;
  return [
    ...mirrorZ([
      { x: barrelLen * 0.45, y, z: 0.24, w: barrelLen, h: 0.13, d: 0.13, color: gunmetal },
      { x: barrelLen * 0.95, y, z: 0.24, w: 0.4, h: 0.09, d: 0.09, color: steel }, // muzzle tips
    ]),
    { x: -0.1, y, z: 0, w: 0.45, h: 0.3, d: 0.75, color: gunmetal }, // receiver block
  ];
}

// Per-class gun muzzle offsets [x, y, z] — one flash per barrel, placed at
// the model's barrel tips. The caliber doctrine sets the count: fifties fly
// quads (a pair per wing, a foot apart), 20 mm cannons fly the classic
// pair, and the heavies are singles — the P-38's centerline chin
// cannon, the F-104's cheek gatling.
export const CLASS_MUZZLES = [
  [ // Spitfire quad fifties
    [2.5, -0.3, 2.95], [2.5, -0.3, 3.25],
    [2.5, -0.3, -2.95], [2.5, -0.3, -3.25],
  ],
  [ // Mustang 20 mm pair
    [3.8, -0.25, 3.2],
    [3.8, -0.25, -3.2],
  ],
  [ // P-38: the lone 30 mm chin cannon on the centerline
    [5.1, -0.62, 0],
  ],
  [ // B-17 chin pair
    [5.9, -0.3, 0.9],
    [5.9, -0.3, -0.9],
  ],
  [ // Avenger quad fifties on the outer panels
    [2.3, -0.42, 3.25], [2.3, -0.42, 3.55],
    [2.3, -0.42, -3.25], [2.3, -0.42, -3.55],
  ],
  [ // F-104: the lone gatling port
    [4.8, -0.18, -0.5],
  ],
  [], // Skywriter: unarmed — the circus never flashes a muzzle
  [], // Marquee: likewise
];

// --- visible battle damage --------------------------------------------------
// The component damage model (engine, wings, fuel, pilot, airframe HP) is
// otherwise read only through smoke/fuel trails. These overlays make a hit
// legible on the airframe itself: a scorched cowl, a shredded wingtip, flak
// holes punched along the fuselage — still chunky boxes, matching the build.

const charBlack = 0x14110e; // soot-blackened panel
const sootEdge = 0x35302a; // singed metal around a burn
const blastHole = 0x070708; // punched-through hole
const tornMetal = 0x2a2d33; // ragged exposed structure / shredded skin
const bareEdge = 0x595e66; // bright torn-aluminium lip
const oilSoot = 0x1a1714; // oil/soot streak blown back from the engine
const fuelStain = 0x15191c; // dark wet fuel weeping back along the belly
const shatterGlass = 0x222a2e; // blown-out, darkened canopy glass
const canopyGlint = 0xaec2cd; // pale glint along a fresh crack in the glass

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const lerpN = (a, b, t) => a + (b - a) * t;

// Tiny deterministic PRNG so a given plane + damage bucket always shreds the
// same way — no flicker between frames or between screenshot runs.
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// A stable 32-bit seed for one bullet hole (roadmap #8): a deterministic mix of
// the entity id, the component it's on, and the hole index — so hole #i on a
// given plane's given component lands in the same spot on every client and in
// the killcam, and new holes accrete beside the old ones as damage climbs.
function holeSeed(id, comp, i) {
  let h = ((id >>> 0) ^ 0x9e3779b1) | 0;
  h = Math.imul(h ^ (comp + 1), 0x85ebca77);
  h = Math.imul(h ^ (i + 1), 0xc2b2ae3d);
  return (h ^ (h >>> 15)) >>> 0;
}

// Per-class anchors for battle damage, in model space (nose +X, up +Y, right
// wing +Z), eyeballed from the silhouettes above. `engine` is the cowl or the
// starboard (out-board) nacelle that takes the scorch on multi-engine types.
const DAMAGE_ANCHORS = [
  { // 0 Spitfire
    engine: { x: 3.5, y: 0, z: 0, r: 0.82 },
    wing: { x: 0.5, y: -0.32, tipZ: 5.3, chord: 1.15, thick: 0.34 },
    fuse: { x0: -1.6, x1: 1.8, y: 0.3, z: 0.62, h: 0.7 },
    canopy: { x: 0.85, y: 1.0, w: 1.3, d: 0.95 },
    tank: { x: 0.4, y: -0.62, len: 2.2 },
    tail: { x: -4.0, y: 0.35, spanZ: 2.2, finY: 1.5 },
  },
  { // 1 Mustang
    engine: { x: 4.5, y: -0.02, z: 0, r: 0.76 },
    wing: { x: 0.6, y: -0.3, tipZ: 5.5, chord: 0.95, thick: 0.3 },
    fuse: { x0: -1.8, x1: 2.4, y: 0.28, z: 0.6, h: 0.65 },
    canopy: { x: -0.35, y: 1.0, w: 1.7, d: 1.0 },
    tank: { x: 0.6, y: -0.58, len: 2.6 },
    tail: { x: -4.5, y: 0.32, spanZ: 1.95, finY: 1.3 },
  },
  { // 2 P-38 (starboard nacelle)
    engine: { x: 2.3, y: -0.32, z: 3.6, r: 0.78 },
    wing: { x: 0.15, y: -0.44, tipZ: 6.9, chord: 1.4, thick: 0.4 },
    fuse: { x0: -2.6, x1: 2.6, y: 0.5, z: 0.86, h: 1.0 },
    canopy: { x: 0.9, y: 1.12, w: 1.7, d: 1.2 },
    tank: { x: 0.2, y: -0.94, len: 3.0 },
    tail: { x: -4.55, y: 0.45, spanZ: 2.9, finY: 1.6 },
  },
  { // 3 B-17 (starboard outboard nacelle)
    engine: { x: 2.4, y: -0.38, z: 5.7, r: 0.78 },
    wing: { x: 0.5, y: -0.4, tipZ: 9.5, chord: 1.7, thick: 0.46 },
    fuse: { x0: -4.0, x1: 3.4, y: 0.6, z: 1.0, h: 1.3 },
    canopy: { x: 2.45, y: 1.32, w: 1.8, d: 1.4 },
    tank: { x: 0.2, y: -1.1, len: 4.0 },
    tail: { x: -6.3, y: 0.55, spanZ: 3.7, finY: 2.6 },
  },
  { // 4 Avenger
    engine: { x: 3.85, y: 0, z: 0, r: 0.95 },
    wing: { x: 0.55, y: -0.36, tipZ: 5.6, chord: 1.3, thick: 0.38 },
    fuse: { x0: -2.4, x1: 2.4, y: 0.42, z: 0.84, h: 0.95 },
    canopy: { x: 1.0, y: 1.05, w: 3.0, d: 1.2 },
    tank: { x: 0.4, y: -0.86, len: 2.6 },
    tail: { x: -4.4, y: 0.5, spanZ: 2.3, finY: 1.9 },
  },
  { // 5 F-104 (the turbojet burns at the tailpipe, not a cowl; T-tail)
    engine: { x: -4.6, y: 0.02, z: 0, r: 0.6 },
    wing: { x: -0.7, y: -0.15, tipZ: 3.7, chord: 1.15, thick: 0.2 },
    fuse: { x0: -3.2, x1: 3.6, y: 0.2, z: 0.58, h: 0.6 },
    canopy: { x: 2.45, y: 0.75, w: 1.3, d: 0.78 },
    tank: { x: 0.2, y: -0.56, len: 2.4 },
    tail: { x: -4.3, y: 2.5, spanZ: 1.6, finY: 2.6 },
  },
  { // 6 Skywriter (fabric burns fast; scorch on the red cowl)
    engine: { x: 3.1, y: 0, z: 0, r: 0.75 },
    wing: { x: 0.7, y: 0.35, tipZ: 5.0, chord: 1.05, thick: 0.28 },
    fuse: { x0: -1.6, x1: 1.6, y: 0.25, z: 0.55, h: 0.6 },
    canopy: { x: 0.9, y: 0.85, w: 0.6, d: 0.9 },
    tank: { x: 0.5, y: -0.55, len: 2.0 },
  },
  { // 7 Marquee (hits pock the gasbag; the "engine" is the gondola)
    engine: { x: 2.6, y: -3.4, z: 0, r: 0.9 },
    wing: { x: 0, y: 0.4, tipZ: 3.0, chord: 4.0, thick: 0.6 },
    fuse: { x0: -8.0, x1: 8.0, y: 0.8, z: 2.9, h: 2.4 },
    canopy: { x: 4.4, y: -3.3, w: 1.0, d: 1.3 },
    tank: { x: -1.0, y: -2.8, len: 5.0 },
  },
];

// Advanced damage: a component this far gone loses its whole section — the
// outer wing shears off, the canopy is blown clean away, the empennage
// lets go and the wreck tumbles nose-heavy.
const WING_LOSE = 0.18; // wing health at/below which the outer panel tears off
const CANOPY_LOSE = 0.16; // pilot health at/below which the glasshouse goes
const TAIL_LOSE = 0.18; // tail health at/below which the empennage tears away
const WING_KEEP = 0.58; // fraction of the half-span that stays attached

// Distance from the centerline where a torn wing shears, per class.
function wingBreakAt(type) {
  return (DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0]).wing.tipZ * WING_KEEP;
}

// Build the overlay boxes (scorch, holes, torn metal) for a damage state.
// d = { engine, wingL, wingR, hp, fuel, pilot, tail } in 0..1 (1 = pristine; missing =
// pristine). entityId seeds the per-plane bullet-hole scatter (roadmap #8) so it
// is deterministic across clients. Returns [] for an essentially undamaged plane.
function damageBoxes(type, d, entityId = 0) {
  const a = DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0];
  const boxes = [];
  const rnd = mulberry32(0x9e3779b1 ^ Math.imul(type + 1, 0x85ebca77));
  const jit = (s) => (rnd() - 0.5) * s;

  // Engine: blackened, soot-streaked cowl / nacelle.
  const eng = clamp01((0.78 - (d.engine ?? 1)) / 0.78);
  if (eng > 0.03) {
    const e = a.engine;
    const k = 0.4 + eng * 0.6;
    boxes.push({ x: e.x + 0.08, y: e.y + e.r * 0.45, z: e.z, w: 1.0, h: e.r * k, d: e.r * 1.75, color: charBlack });
    boxes.push({ x: e.x - 0.15, y: e.y, z: e.z + e.r * 0.85, w: 1.05, h: e.r * 1.3, d: 0.16, color: sootEdge });
    boxes.push({ x: e.x - 0.15, y: e.y, z: e.z - e.r * 0.85, w: 1.05, h: e.r * 1.3, d: 0.16, color: sootEdge });
    const streaks = 2 + Math.round(eng * 2);
    for (let i = 0; i < streaks; i++) {
      const len = 1.4 + rnd() * 2.4 * (0.6 + eng);
      boxes.push({ x: e.x - 0.8 - len / 2, y: e.y + e.r * 0.5 + jit(0.18), z: e.z + jit(e.r * 1.1), w: len, h: 0.1, d: 0.16 + rnd() * 0.12, color: oilSoot });
    }
  }

  // Wings: shredded, blackened tip on whichever side was hit — and a ragged
  // stump at the shear line once the outer panel has been torn clean away.
  for (const [side, h] of [[1, d.wingR ?? 1], [-1, d.wingL ?? 1]]) {
    const sev = clamp01((0.7 - h) / 0.7);
    if (sev <= 0.03) continue;
    const w = a.wing;
    if (h <= WING_LOSE) {
      // The outer wing is gone (clipped from the body): cap the break with a
      // charred, ragged stump and a glint of exposed spar.
      const cut = wingBreakAt(type);
      boxes.push({ x: w.x, y: w.y, z: side * cut, w: w.chord * 1.7, h: w.thick * 1.3, d: 0.4, color: charBlack });
      boxes.push({ x: w.x - w.chord * 0.2, y: w.y, z: side * (cut - 0.18), w: w.chord * 0.55, h: w.thick * 0.6, d: 0.3, color: bareEdge });
      for (let i = 0; i < 5; i++) {
        boxes.push({
          x: w.x + jit(w.chord * 1.3),
          y: w.y + jit(w.thick),
          z: side * cut + side * (rnd() * 0.45 - 0.1) + jit(0.25),
          w: 0.25 + rnd() * w.chord * 0.6,
          h: w.thick * (0.5 + rnd()),
          d: 0.16 + rnd() * 0.35,
          color: i % 2 ? tornMetal : charBlack,
        });
      }
      continue;
    }
    const z0 = side * w.tipZ;
    const span = w.tipZ * (0.16 + sev * 0.4);
    // Charred outer panel creeping inboard from the tip (top + underside).
    boxes.push({ x: w.x, y: w.y + w.thick * 0.55, z: side * (w.tipZ - span * 0.5), w: w.chord * 1.9, h: w.thick * 0.55, d: span, color: charBlack });
    boxes.push({ x: w.x, y: w.y - w.thick * 0.55, z: side * (w.tipZ - span * 0.5), w: w.chord * 1.7, h: w.thick * 0.45, d: span, color: sootEdge });
    // Ragged teeth where the tip was blown off.
    const teeth = 3 + Math.round(sev * 4);
    for (let i = 0; i < teeth; i++) {
      const t = i / teeth;
      boxes.push({
        x: w.x + jit(w.chord * 1.3),
        y: w.y + jit(w.thick),
        z: z0 - side * t * span * 0.7 + jit(0.3),
        w: 0.3 + rnd() * w.chord * 0.7,
        h: w.thick * (0.5 + rnd()),
        d: 0.18 + rnd() * 0.4,
        color: i % 2 ? tornMetal : charBlack,
      });
    }
    // A blown-through hole mid-outer wing once it is badly hit.
    if (sev > 0.45) {
      const hz = side * w.tipZ * (0.5 + jit(0.08));
      boxes.push({ x: w.x + jit(0.4), y: w.y, z: hz, w: w.chord * 0.9, h: w.thick * 1.5, d: 0.5 + sev * 0.6, color: blastHole });
      boxes.push({ x: w.x + jit(0.4), y: w.y + w.thick * 0.45, z: hz, w: w.chord * 1.25, h: 0.1, d: 0.9, color: bareEdge });
    }
  }

  // Tail: chewed-up elevators and a shot-through fin — and once the whole
  // empennage has torn away, a ragged charred stump at the break.
  const tailv = clamp01((0.7 - (d.tail ?? 1)) / 0.7);
  if (tailv > 0.03) {
    const tl = a.tail;
    if ((d.tail ?? 1) <= TAIL_LOSE) {
      // The empennage is gone (clipped from the body): a torn, blackened
      // break where the boom ends, spar stubs poking aft.
      const cut = tl.x + 0.55;
      boxes.push({ x: cut, y: tl.y * 0.6, z: 0, w: 0.5, h: 0.9, d: 0.9, color: charBlack });
      boxes.push({ x: cut - 0.2, y: tl.y * 0.6 + 0.15, z: 0, w: 0.35, h: 0.4, d: 0.5, color: bareEdge });
      for (let i = 0; i < 4; i++) {
        boxes.push({
          x: cut - 0.15 - rnd() * 0.4,
          y: tl.y * 0.6 + jit(0.6),
          z: jit(0.7),
          w: 0.2 + rnd() * 0.5,
          h: 0.1 + rnd() * 0.2,
          d: 0.1 + rnd() * 0.2,
          color: i % 2 ? tornMetal : charBlack,
        });
      }
    } else {
      // Charred elevator tips creeping inboard, soot up the fin.
      const span = tl.spanZ * (0.2 + tailv * 0.45);
      for (const side of [1, -1]) {
        boxes.push({ x: tl.x, y: tl.y + 0.16, z: side * (tl.spanZ - span * 0.5), w: 1.1, h: 0.14, d: span, color: charBlack });
      }
      boxes.push({ x: tl.x - 0.3, y: (tl.y + tl.finY) * 0.5, z: 0, w: 0.5, h: (tl.finY - tl.y) * (0.3 + tailv * 0.5), d: 0.3, color: sootEdge });
      // Ragged bites out of the elevator trailing edge once it's badly hit.
      if (tailv > 0.45) {
        const teeth = 2 + Math.round(tailv * 3);
        for (let i = 0; i < teeth; i++) {
          const sgn = i % 2 ? 1 : -1;
          boxes.push({
            x: tl.x - 0.4 + jit(0.3),
            y: tl.y + jit(0.12),
            z: sgn * (0.5 + rnd() * (tl.spanZ - 0.5)),
            w: 0.25 + rnd() * 0.4,
            h: 0.3,
            d: 0.2 + rnd() * 0.35,
            color: i % 2 ? blastHole : tornMetal,
          });
        }
      }
    }
  }

  // Airframe HP: scorch patches and flak holes scattered down the fuselage.
  const hpv = clamp01((0.7 - (d.hp ?? 1)) / 0.7);
  if (hpv > 0.03) {
    const f = a.fuse;
    const n = 2 + Math.round(hpv * 5);
    for (let i = 0; i < n; i++) {
      const x = lerpN(f.x0, f.x1, rnd());
      const side = rnd() > 0.5 ? 1 : -1;
      const onTop = rnd() > 0.6;
      const y = onTop ? f.y + f.h * 0.55 : f.y + jit(f.h);
      const z = onTop ? jit(f.z * 0.8) : side * (f.z + 0.03);
      const s = 0.35 + rnd() * 0.6;
      boxes.push({
        x, y, z,
        w: s,
        h: onTop ? 0.12 : s * 0.9,
        d: onTop ? s : 0.12,
        color: i % 3 ? charBlack : sootEdge,
      });
      if (hpv > 0.4 && rnd() > 0.55) {
        boxes.push({
          x: x + jit(0.12), y, z: z + (onTop ? 0 : side * 0.04),
          w: s * 0.55,
          h: onTop ? 0.14 : s * 0.5,
          d: onTop ? s * 0.55 : 0.14,
          color: blastHole,
        });
      }
    }
  }

  // Fuel: a dark wet streak weeping back along the belly from the punctured tank.
  const fuelv = clamp01((0.85 - (d.fuel ?? 1)) / 0.85);
  if (fuelv > 0.05) {
    const tk = a.tank;
    const n = 2 + Math.round(fuelv * 3);
    for (let i = 0; i < n; i++) {
      const back = (i / n) * tk.len;
      boxes.push({ x: tk.x - back, y: tk.y - 0.03, z: jit(0.5), w: 0.5 + rnd() * 0.6, h: 0.08, d: 0.16 + rnd() * 0.22, color: fuelStain });
    }
  }

  // Pilot: a shattered canopy where the cockpit took a round — or, once the
  // glasshouse has been blown clean off, a torn, empty coaming.
  const pilotv = clamp01((0.6 - (d.pilot ?? 1)) / 0.6);
  if (pilotv > 0.05) {
    const cp = a.canopy;
    if ((d.pilot ?? 1) <= CANOPY_LOSE) {
      // Glass blown clean off: torn frame rails fore and aft of an open, dark
      // cockpit, ringed with jagged shattered-glass remnants still in the sill.
      const rim = cp.d * 0.5;
      boxes.push({ x: cp.x + cp.w * 0.46, y: cp.y - 0.02, z: 0, w: 0.2, h: 0.36, d: cp.d * 0.92, color: tornMetal }); // front coaming
      boxes.push({ x: cp.x - cp.w * 0.46, y: cp.y - 0.05, z: 0, w: 0.2, h: 0.3, d: cp.d * 0.86, color: charBlack }); // rear coaming
      boxes.push({ x: cp.x, y: cp.y - 0.18, z: 0, w: cp.w * 0.7, h: 0.22, d: cp.d * 0.7, color: blastHole }); // dark open tub
      const shards = 9;
      for (let i = 0; i < shards; i++) {
        const sgn = i % 2 ? 1 : -1;
        const along = (i / shards - 0.5) * cp.w;
        boxes.push({
          x: cp.x + along + jit(0.12),
          y: cp.y + 0.08 + rnd() * 0.28,
          z: sgn * rim * (0.82 + rnd() * 0.26),
          w: 0.12 + rnd() * 0.16,
          h: 0.24 + rnd() * 0.3,
          d: 0.06,
          color: i % 3 === 0 ? canopyGlint : glassDeep, // jagged glass teeth
        });
      }
    } else {
      // Blown-out, darkened glass sitting proud of the frame.
      boxes.push({ x: cp.x, y: cp.y + 0.08, z: 0, w: cp.w * 0.95, h: 0.18, d: cp.d * 0.95, color: shatterGlass });
      // The hole the round punched through.
      boxes.push({ x: cp.x + jit(cp.w * 0.3), y: cp.y + 0.13, z: jit(cp.d * 0.25), w: cp.w * 0.32, h: 0.22, d: cp.d * 0.32, color: blastHole });
      // A spider-web of fractures — dark splits plus a few pale glints so it
      // reads as shattered glass, not just a dark panel.
      const cracks = 4 + Math.round(pilotv * 4);
      for (let i = 0; i < cracks; i++) {
        const col = i % 3 === 0 ? canopyGlint : charBlack;
        if (i % 2 === 0) {
          boxes.push({ x: cp.x + jit(cp.w * 0.6), y: cp.y + 0.17, z: jit(cp.d * 0.35), w: cp.w * (0.3 + rnd() * 0.6), h: 0.05, d: 0.05, color: col });
        } else {
          boxes.push({ x: cp.x + jit(cp.w * 0.4), y: cp.y + 0.17, z: jit(cp.d * 0.45), w: 0.05, h: 0.05, d: cp.d * (0.3 + rnd() * 0.6), color: col });
        }
      }
      // A couple of jagged shards still standing in the frame.
      for (let i = 0; i < 3; i++) {
        boxes.push({ x: cp.x + jit(cp.w * 0.7), y: cp.y + 0.22 + rnd() * 0.12, z: jit(cp.d * 0.5), w: 0.1 + rnd() * 0.14, h: 0.14 + rnd() * 0.16, d: 0.06, color: i % 2 ? shatterGlass : tornMetal });
      }
    }
  }

  // Bullet pocks (roadmap #8): a deterministic scatter of small dark holes over
  // each battle-damaged component. Independent of the scorch/tear overlay above
  // — this is the fine texture of getting shot up, and it clears automatically
  // when a deck repair climbs the subsystem bytes back. Each hole is seeded by
  // (entityId, component, index) so it lands identically on every client and
  // accretes beside its neighbours as the component takes more fire.
  const HOLES = 7; // per subsystem at zero health
  const holes = (comp, health, place) => {
    const n = Math.floor((1 - clamp01(health ?? 1)) * HOLES);
    for (let i = 0; i < n; i++) {
      const r = mulberry32(holeSeed(entityId, comp, i));
      const s = 0.12 + r() * 0.08;
      const p = place(r);
      boxes.push({ x: p.x, y: p.y, z: p.z, w: s, h: s, d: s, color: blastHole });
    }
  };
  const eA = a.engine, wA = a.wing, fA = a.fuse, tA = a.tank, cA = a.canopy;
  holes(1, d.engine, (r) => ({ // engine cowl / nacelle
    x: eA.x + (r() - 0.5) * 0.9, y: eA.y + (r() - 0.5) * eA.r * 1.4, z: eA.z + (r() - 0.5) * eA.r * 1.6,
  }));
  for (const [comp, health, side] of [[3, d.wingR, 1], [2, d.wingL, -1]]) {
    holes(comp, health, (r) => ({
      x: wA.x + (r() - 0.5) * wA.chord * 1.4,
      y: wA.y + (r() - 0.5) * wA.thick * 1.3,
      z: side * (0.9 + r() * (wA.tipZ * 0.95 - 0.9)),
    }));
  }
  holes(4, d.fuel, (r) => ({ // punctured tank / belly
    x: tA.x - r() * tA.len, y: tA.y + (r() - 0.5) * 0.3, z: (r() - 0.5) * fA.z * 1.3,
  }));
  holes(5, d.pilot, (r) => ({ // cockpit surround
    x: cA.x + (r() - 0.5) * cA.w, y: cA.y + (r() - 0.4) * 0.4, z: (r() - 0.5) * cA.d,
  }));
  const tlA = a.tail;
  holes(6, d.tail, (r) => ({ // tailplane + fin root
    x: tlA.x + (r() - 0.5) * 1.4,
    y: tlA.y + r() * (tlA.finY - tlA.y) * 0.5,
    z: (r() - 0.5) * tlA.spanZ * 1.7,
  }));
  // Structural (airframe HP): general fuselage, a handful scaling with the hp
  // bucket (capped shorter than a subsystem — the fuselage is broad but tough).
  const hpN = Math.floor((1 - clamp01(d.hp ?? 1)) * 4);
  for (let i = 0; i < hpN; i++) {
    const r = mulberry32(holeSeed(entityId, 0, i));
    const s = 0.13 + r() * 0.08;
    const sd = r() > 0.5 ? 1 : -1;
    boxes.push({ x: lerpN(fA.x0, fA.x1, r()), y: fA.y + (r() - 0.5) * fA.h, z: sd * (fA.z + 0.02), w: s, h: s, d: s, color: blastHole });
  }

  return boxes;
}

// Earnable paint schemes (roadmap #25). The config array is handed in at boot
// via setSchemes(); each entry repaints the camouflage and trim and optionally a
// fin stripe. None of it says which side you fly for — the nameplate does.
//
// A livery is a scheme painted for one airframe (`model`: the Bf 109's
// Galland and Trautloft plates, the Spitfire's YQ-P, the Corsair's three, the
// Mustang's Shangri-La and Sunshine, the Lightning's Putt Putt Maru and
// Frances' Rebel, the Fw 190's Romm's D-9 and Yellow 5): a color per marking
// (`paint`) and its own decals (`livery`). On any other airframe it is the
// factory finish, and the PAINT SHOP only offers it when that airframe is
// picked.
let SCHEMES = [];
export function setSchemes(list) {
  SCHEMES = Array.isArray(list) ? list : [];
}

/** The name a class's factory finish goes by in the PAINT SHOP when its
 * model was painted off a plate (the Mustang's is Pattie Ann), else null. */
export function factoryFinish(type) {
  return AIRCRAFT_MODELS[CLASS_MODELS[type]]?.finish ?? null;
}

/** The model family class `type` belongs to: its office, its liveries and
 * the schemes painted for it (the Mk XVI's clipped wing is a Spitfire's). */
export function classFamily(type) {
  return modelFamily(CLASS_MODELS[type]);
}

/** Does scheme `def` paint class `type`? (A livery fits its own model only.) */
export function schemeFits(def, type) {
  return !!def && (!def.model || def.model === classFamily(type));
}

// A per-class fin box the tail stripe paints when a scheme sets one. Reuses each
// builder's fin coordinates so the stripe sits on the actual rudder/fin (the
// P-38 has twin fins, hence two boxes).
const TAIL_STRIPE_ANCHORS = [
  [{ x: -4.42, y: 1.12, z: 0, w: 0.5, h: 0.44, d: 0.34 }],                                  // 0 Spitfire
  [{ x: -4.78, y: 1.18, z: 0, w: 0.52, h: 0.44, d: 0.3 }],                                  // 1 Mustang
  [{ x: -4.55, y: 1.3, z: 2.7, w: 1.05, h: 0.44, d: 0.36 }, { x: -4.55, y: 1.3, z: -2.7, w: 1.05, h: 0.44, d: 0.36 }], // 2 P-38 (twin fins)
  [{ x: -6.3, y: 1.75, z: 0, w: 0.72, h: 0.5, d: 0.42 }],                                   // 3 B-17
  [{ x: -4.75, y: 1.55, z: 0, w: 0.6, h: 0.5, d: 0.3 }],                                    // 4 Avenger
  [{ x: -4.72, y: 1.62, z: 0, w: 0.52, h: 0.5, d: 0.26 }],                                  // 5 F-104
];

// --- the rest of the hangar (classes 8–16) ----------------------------------
// The nine airframes that waited in art/ as spares until the roster took real
// names. Their far-level silhouettes come off two builders — a single-engine
// fighter and a twin — sized from each model's own node bounds (the same
// numbers hitbox.go's silhouettes are cut from), so past 2 km the box is the
// same airplane in the same paint, and under ?boxes it still reads as a 109
// or a Heinkel rather than a Spitfire in the wrong coat.

// A single-engine fighter. `s` is in the model's metres: the tail and spinner
// stations, fuselage height and width, the wing's span, chord and station,
// the canopy's run, the tail group, and the wing guns [x, z].
function singleBoxes(t, s) {
  const { tail: L0, nose: L1, h, d } = s;
  const xm0 = L0 + (L1 - L0) * 0.42; // where the rear taper starts
  const xm1 = L1 - 1.8; // where the engine bay starts
  const [c0, c1] = s.canopy;
  const tl = s.tailGroup;
  const cowl = s.radial ? dark : t.body;
  const boxes = [
    // Fuselage: engine bay, cockpit section, two taper slabs, shaded belly.
    { x: (xm1 + L1) / 2, y: 0, z: 0, w: L1 - xm1, h, d, color: cowl },
    { x: (xm0 + xm1) / 2, y: 0, z: 0, w: xm1 - xm0, h, d, color: t.body },
    { x: xm0 - (xm0 - L0) * 0.25, y: 0.06, z: 0, w: (xm0 - L0) * 0.5, h: h * 0.8, d: d * 0.8, color: t.body },
    { x: L0 + (xm0 - L0) * 0.25, y: 0.14, z: 0, w: (xm0 - L0) * 0.5, h: h * 0.58, d: d * 0.6, color: t.body },
    { x: (xm0 + L1) / 2, y: -h / 2 + 0.1, z: 0, w: (L1 - xm0) * 0.9, h: 0.24, d: d * 0.8, color: shade(t.body, 0.72) },
    // Spinner (and a radial's cowl lip).
    { x: L1 + 0.25, y: 0, z: 0, w: 0.55, h: 0.6, d: 0.6, color: steel },
    ...(s.radial ? [{ x: L1 - 0.12, y: 0, z: 0, w: 0.3, h: h * 1.02, d: d * 1.02, color: gunmetal }] : []),
    ...mirrorZ([{ x: L1 - 1.1, y: 0.08, z: d / 2 + 0.04, w: 1.3, h: 0.2, d: 0.14, color: exhaustBrown }]),
    // Canopy: windscreen frame, glasshouse, crown, and the spine running aft.
    { x: c1, y: h / 2 + 0.2, z: 0, w: 0.24, h: 0.5, d: d * 0.75, color: t.body },
    { x: (c0 + c1) / 2, y: h / 2 + 0.26, z: 0, w: c1 - c0, h: 0.58, d: d * 0.7, color: glass },
    { x: (c0 + c1) / 2, y: h / 2 + 0.58, z: 0, w: (c1 - c0) * 0.8, h: 0.1, d: d * 0.4, color: glassDeep },
    ...(s.bubble ? [] : [{ x: (L0 + 1 + c0) / 2, y: h / 2 + 0.05, z: 0, w: c0 - L0 - 1, h: 0.22, d: d * 0.5, color: t.body }]),
    // Wings: three stacked slabs of shrinking thickness fake the taper.
    { x: s.wingX, y: s.wingY, z: 0, w: s.chord * 1.1, h: 0.38, d: s.span * 0.66, color: t.color },
    { x: s.wingX - 0.04, y: s.wingY + 0.02, z: 0, w: s.chord * 0.95, h: 0.32, d: s.span * 0.84, color: t.color },
    { x: s.wingX - 0.08, y: s.wingY + 0.04, z: 0, w: s.chord * 0.78, h: 0.26, d: s.span, color: t.color },
    ...mirrorZ([
      { x: s.wingX - 0.1, y: s.wingY + 0.05, z: s.span / 2 - 0.3, w: s.chord * 0.7, h: 0.3, d: 0.6, color: t.accent }, // tip caps
      { x: s.wingX - s.chord * 0.55, y: s.wingY + 0.02, z: s.span * 0.34, w: 0.45, h: 0.24, d: s.span * 0.2, color: shade(t.color, 0.78) }, // aileron
      ...s.guns.flatMap(([gx, gz]) => [
        { x: gx, y: s.wingY - 0.02, z: gz, w: 1.0, h: 0.2, d: 0.15, color: gunmetal },
        { x: gx + 0.75, y: s.wingY - 0.02, z: gz, w: 0.5, h: 0.12, d: 0.12, color: steel },
      ]),
      ...(s.rockets ? [2.3, 2.7].map((z) => ({ x: s.wingX + 0.2, y: s.wingY - 0.3, z, w: 1.5, h: 0.16, d: 0.16, color: steel })) : []),
    ]),
    { x: s.wingX - 0.1, y: s.wingY + 0.1, z: s.span / 2 + 0.05, w: 0.26, h: 0.26, d: 0.26, color: navGreen },
    { x: s.wingX - 0.1, y: s.wingY + 0.1, z: -s.span / 2 - 0.05, w: 0.26, h: 0.26, d: 0.26, color: navRed },
    // Tail group: tailplane and elevator, fin and rudder, tip caps.
    { x: tl.x, y: 0.32, z: 0, w: 1.2, h: 0.22, d: tl.span, color: t.color },
    { x: tl.x - 0.58, y: 0.32, z: 0, w: 0.34, h: 0.18, d: tl.span * 0.9, color: shade(t.color, 0.78) },
    { x: tl.x + 0.1, y: (0.3 + tl.finTop) / 2 + 0.1, z: 0, w: 1.0, h: tl.finTop - 0.3, d: 0.26, color: t.body },
    { x: tl.x - 0.5, y: (0.3 + tl.finTop) / 2 + 0.15, z: 0, w: 0.46, h: tl.finTop - 0.25, d: 0.24, color: t.color },
    { x: tl.x - 0.05, y: tl.finTop + 0.05, z: 0, w: 0.7, h: 0.24, d: 0.3, color: t.accent },
    { x: tl.x + 0.3, y: -h / 2, z: 0, w: 0.28, h: 0.46, d: 0.2, color: tire },
  ];
  // The Warhawk's shark mouth: the chin scoop painted to bite.
  if (s.shark) {
    boxes.push(
      { x: L1 - 0.9, y: -h / 2 + 0.02, z: 0, w: 1.3, h: 0.34, d: d * 0.92, color: 0xb22a2e },
      ...mirrorZ([{ x: L1 - 0.9, y: -h / 2 + 0.2, z: d * 0.47, w: 1.1, h: 0.08, d: 0.06, color: 0xf2f2ed }]),
    );
  }
  return boxes;
}

// A twin: two nacelles on the wing (or the Me 262's jet pods slung under it),
// a glazed nose or a gun nose, a single fin or a pair at the tailplane tips.
function twinBoxes(t, s) {
  const { tail: L0, nose: L1, h, d } = s;
  const [n0, n1] = s.nacelle;
  const [c0, c1] = s.canopy;
  const tl = s.tailGroup;
  const xm0 = L0 + (L1 - L0) * 0.38;
  const noseX = s.glassNose ? s.glassNose[0] : L1;
  const podH = s.jet ? 0.95 : 1.25;
  const boxes = [
    // Fuselage: the main slab, two taper slabs, the shaded belly.
    { x: (xm0 + noseX) / 2, y: 0, z: 0, w: noseX - xm0, h, d, color: t.body },
    { x: xm0 - (xm0 - L0) * 0.25, y: 0.1, z: 0, w: (xm0 - L0) * 0.5, h: h * 0.78, d: d * 0.78, color: t.body },
    { x: L0 + (xm0 - L0) * 0.25, y: 0.2, z: 0, w: (xm0 - L0) * 0.5, h: h * 0.55, d: d * 0.56, color: t.body },
    { x: (xm0 + noseX) / 2, y: -h / 2 + 0.1, z: 0, w: (noseX - xm0) * 0.9, h: 0.26, d: d * 0.84, color: shade(t.body, 0.72) },
    // The cockpit glass (the whole nose, on a Junkers or a Heinkel).
    { x: (c0 + c1) / 2, y: h / 2 + 0.24, z: 0, w: c1 - c0, h: 0.6, d: d * 0.72, color: glass },
    { x: (c0 + c1) / 2, y: h / 2 + 0.56, z: 0, w: (c1 - c0) * 0.9, h: 0.1, d: d * 0.76, color: t.body },
    ...(s.glassNose
      ? [
          { x: (s.glassNose[0] + s.glassNose[1]) / 2, y: 0.05, z: 0, w: s.glassNose[1] - s.glassNose[0], h: h * 0.9, d: d * 0.9, color: glass },
          { x: s.glassNose[1] - 0.2, y: 0, z: 0, w: 0.4, h: h * 0.6, d: d * 0.6, color: glassDeep },
        ]
      : []),
    // Nose guns on the centerline.
    ...(s.noseGuns ?? []).map(([gx, gy]) => ({ x: gx, y: gy, z: 0, w: 0.8, h: 0.14, d: 0.3, color: gunmetal })),
    // Wings: three taper slabs.
    { x: s.wingX, y: s.wingY, z: 0, w: s.chord * 1.1, h: 0.44, d: s.span * 0.62, color: t.color },
    { x: s.wingX - 0.05, y: s.wingY + 0.02, z: 0, w: s.chord * 0.95, h: 0.37, d: s.span * 0.82, color: t.color },
    { x: s.wingX - 0.1, y: s.wingY + 0.04, z: 0, w: s.chord * 0.78, h: 0.3, d: s.span, color: t.color },
    ...mirrorZ([
      { x: s.wingX - 0.1, y: s.wingY + 0.05, z: s.span / 2 - 0.35, w: s.chord * 0.7, h: 0.34, d: 0.7, color: t.accent }, // tip caps
      { x: s.wingX - s.chord * 0.55, y: s.wingY + 0.02, z: s.span * 0.36, w: 0.5, h: 0.26, d: s.span * 0.2, color: shade(t.color, 0.78) },
      // Nacelle: the long pod, its intake or cowl ring, and the tail cone.
      { x: (n0 + n1) / 2, y: s.nacY, z: s.nacZ, w: n1 - n0, h: podH, d: podH, color: s.jet ? t.body : dark },
      { x: n1 - 0.12, y: s.nacY, z: s.nacZ, w: 0.26, h: podH * 1.06, d: podH * 1.06, color: gunmetal },
      ...(s.jet
        ? [{ x: n0 + 0.2, y: s.nacY, z: s.nacZ, w: 0.4, h: podH * 0.6, d: podH * 0.6, color: dark }]
        : [
            { x: n1 + 0.2, y: s.nacY, z: s.nacZ, w: 0.5, h: 0.55, d: 0.55, color: steel },
            { x: n1 - 1.3, y: s.nacY + 0.2, z: s.nacZ + podH / 2 + 0.02, w: 1.1, h: 0.18, d: 0.14, color: exhaustBrown },
          ]),
      ...(s.torpedo ? [{ x: 0.5, y: -h / 2 - 0.3, z: 1.55, w: 5.2, h: 0.46, d: 0.46, color: 0x5a5e62 }] : []),
    ]),
    { x: s.wingX - 0.1, y: s.wingY + 0.12, z: s.span / 2 + 0.05, w: 0.3, h: 0.3, d: 0.3, color: navGreen },
    { x: s.wingX - 0.1, y: s.wingY + 0.12, z: -s.span / 2 - 0.05, w: 0.3, h: 0.3, d: 0.3, color: navRed },
    // Tail group.
    { x: tl.x, y: 0.4, z: 0, w: 1.5, h: 0.26, d: tl.span, color: t.color },
    { x: tl.x - 0.7, y: 0.4, z: 0, w: 0.4, h: 0.2, d: tl.span * 0.9, color: shade(t.color, 0.78) },
    { x: tl.x + 0.5, y: -h / 2 + 0.2, z: 0, w: 0.3, h: 0.5, d: 0.22, color: tire },
  ];
  const finH = tl.finTop - 0.4;
  const fins = tl.finZ ? [tl.finZ, -tl.finZ] : [0];
  for (const fz of fins) {
    boxes.push(
      { x: tl.x + 0.1, y: 0.4 + finH / 2, z: fz, w: 1.3, h: finH, d: 0.3, color: fz ? t.color : t.body },
      { x: tl.x - 0.55, y: 0.45 + finH / 2, z: fz, w: 0.5, h: finH, d: 0.28, color: t.color },
      { x: tl.x - 0.1, y: tl.finTop + 0.05, z: fz, w: 0.9, h: 0.26, d: 0.34, color: t.accent },
    );
  }
  return boxes;
}

// Everything the per-class tables say about each new airframe, keyed by its
// model stem (CLASS_MODEL) and wired in by index below. Dimensions are the
// models' own: `tail`/`nose` are the fuselage's aft end and propeller plane,
// `tailGroup.x` the tailplane's centre, `finTop` the fin's crown.
const HANGAR = {
  bf109: {
    kind: "single", tail: -4.8, nose: 3.3, h: 1.4, d: 0.94, span: 9.96, chord: 1.7, wingX: 0.05, wingY: -0.45,
    canopy: [-0.25, 1.62], tailGroup: { x: -4.4, span: 3.68, finTop: 1.66 }, guns: [],
    livery: { body: 0x9eadb3, color: 0x4d5755, accent: 0xe0a62c }, // RLM 74 over 76 sides, the RLM 04 band
    muzzles: [[4.0, 0, 0]], // the hub cannon, down the spinner
  },
  fw190: {
    kind: "single", radial: true, tail: -5.05, nose: 3.3, h: 1.4, d: 1.32, span: 10.54, chord: 1.9, wingX: 0.08, wingY: -0.45,
    canopy: [-1.4, 0.55], bubble: true, tailGroup: { x: -4.4, span: 3.8, finTop: 1.72 }, guns: [[1.0, 3.22]],
    livery: { body: 0x9eadb3, color: 0x62666b, accent: 0xe0a62c }, // Kittel's 75 over 76 flanks, the RLM 04 band and chin
    muzzles: [[1.9, -0.45, 3.22], [1.9, -0.45, -3.22]],
  },
  p40n: {
    kind: "single", shark: true, tail: -5.9, nose: 3.55, h: 1.4, d: 0.9, span: 11.4, chord: 2.0, wingX: 0.0, wingY: -0.4,
    canopy: [-0.75, 0.55], tailGroup: { x: -5.1, span: 4.52, finTop: 1.87 }, guns: [[0.9, 3.04], [0.9, 3.34]],
    livery: { body: 0x4c5233, color: 0x4c5233, accent: 0xf2f2ed }, // olive drab, insignia white
    muzzles: [[1.7, -0.42, 3.04], [1.7, -0.42, 3.34], [1.7, -0.42, -3.04], [1.7, -0.42, -3.34]],
  },
  f4u: {
    kind: "single", radial: true, rockets: true, tail: -6.55, nose: 3.3, h: 1.5, d: 1.38, span: 12.56, chord: 2.1, wingX: -0.1, wingY: -0.62,
    canopy: [-2.35, -0.15], bubble: true, tailGroup: { x: -5.7, span: 4.7, finTop: 1.9 }, guns: [[0.8, 3.35], [0.8, 3.65]],
    livery: { body: 0x24304a, color: 0x24304a, accent: 0xe8e6dc }, // glossy sea blue
    muzzles: [[1.6, -0.64, 3.35], [1.6, -0.64, 3.65], [1.6, -0.64, -3.35], [1.6, -0.64, -3.65]],
  },
  bf110: {
    kind: "twin", tail: -7.35, nose: 4.5, h: 1.5, d: 1.16, span: 16.3, chord: 2.6, wingX: 0.0, wingY: -0.3,
    canopy: [-1.65, 3.05], nacelle: [-1.95, 3.7], nacZ: 2.6, nacY: -0.1, noseGuns: [[4.4, 0.26]],
    tailGroup: { x: -6.5, span: 5.04, finTop: 1.38, finZ: 2.52 },
    livery: { body: 0x848a8d, color: 0x505659, accent: 0xe8e6dc },
    muzzles: [[4.8, 0.26, 0]],
  },
  a20: {
    kind: "twin", tail: -8.3, nose: 6.35, h: 2.0, d: 1.36, span: 18.8, chord: 3.3, wingX: 0.7, wingY: 0.5,
    canopy: [1.9, 4.25], nacelle: [-2.63, 4.19], nacZ: 2.85, nacY: 0.3, noseGuns: [[6.2, 0.18], [6.2, -0.18]],
    tailGroup: { x: -7.1, span: 7.5, finTop: 3.02 },
    livery: { body: 0x4c5233, color: 0x4c5233, accent: 0xe8e6dc },
    muzzles: [[6.7, 0, 0]],
  },
  ju88: {
    kind: "twin", torpedo: true, tail: -8.3, nose: 5.9, h: 2.1, d: 1.76, span: 20.1, chord: 3.2, wingX: 0.0, wingY: 0.0,
    canopy: [3.18, 5.4], glassNose: [4.6, 5.9], nacelle: [-2.6, 3.62], nacZ: 2.65, nacY: 0.0,
    tailGroup: { x: -7.1, span: 7.96, finTop: 2.85 },
    livery: { body: 0x3f4b39, color: 0x3f4b39, accent: 0xe8e6dc }, // splinter green over light blue
    muzzles: [[6.3, 0.2, -0.12]],
  },
  he111: {
    kind: "twin", tail: -9.1, nose: 7.3, h: 2.1, d: 1.84, span: 22.8, chord: 4.4, wingX: 0.1, wingY: -0.1,
    canopy: [5.3, 6.4], glassNose: [5.3, 7.3], nacelle: [-3.0, 4.2], nacZ: 3.2, nacY: -0.1,
    tailGroup: { x: -8.0, span: 8.3, finTop: 3.2 },
    livery: { body: 0x3f4b39, color: 0x3f4b39, accent: 0xe8e6dc },
    muzzles: [[7.75, 0.1, 0]],
  },
  me262: {
    kind: "twin", jet: true, tail: -5.75, nose: 4.95, h: 1.34, d: 1.32, span: 12.6, chord: 2.9, wingX: -0.3, wingY: -0.2,
    canopy: [-1.1, 1.3], nacelle: [-2.6, 2.25], nacZ: 2.0, nacY: -0.79, noseGuns: [[4.5, 0.25], [4.5, 0.05]],
    tailGroup: { x: -5.0, span: 4.7, finTop: 2.18 },
    livery: { body: 0x7c836f, color: 0x5b5a47, accent: 0xe8e6dc }, // mottled grey-green over dark brown
    muzzles: [[4.8, 0.15, 0]],
  },
};

// The box-level damage anchors (DAMAGE_ANCHORS' shape) for a hangar spec.
function hangarAnchors(s) {
  const [c0, c1] = s.canopy;
  const twin = s.kind === "twin";
  return {
    engine: twin
      ? { x: s.jet ? s.nacelle[0] + 0.6 : (s.nacelle[0] + s.nacelle[1]) / 2 + 0.8, y: s.nacY, z: s.nacZ, r: 0.8 }
      : { x: s.nose - 0.9, y: 0, z: 0, r: s.h * 0.58 },
    wing: { x: s.wingX, y: s.wingY, tipZ: s.span / 2 - 0.2, chord: s.chord * 0.5, thick: twin ? 0.4 : 0.32 },
    fuse: { x0: s.tail * 0.42, x1: s.nose * 0.42, y: 0.3, z: s.d / 2, h: s.h * 0.6 },
    canopy: { x: (c0 + c1) / 2, y: s.h / 2 + 0.45, w: Math.min(3, c1 - c0), d: s.d * 0.8 },
    tank: { x: s.wingX, y: -s.h / 2, len: twin ? 3.0 : 2.4 },
    tail: { x: s.tailGroup.x, y: 0.35, spanZ: s.tailGroup.span / 2, finY: s.tailGroup.finTop },
  };
}

CLASS_MODEL.forEach((stem, i) => {
  const s = HANGAR[stem];
  if (!s) return;
  CLASS_BUILDERS[i] = s.kind === "twin" ? (t) => twinBoxes(t, s) : (t) => singleBoxes(t, s);
  CLASS_LIVERY[i] = s.livery;
  CLASS_PROPS[i] = s.jet ? [] : s.kind === "twin" ? [[s.nacelle[1] + 0.2, s.nacZ], [s.nacelle[1] + 0.2, -s.nacZ]] : [[s.nose + 0.1, 0]];
  if (s.jet) CLASS_JET.add(i);
  // With the models flying, applyModelTrails has already hung these off the
  // model's own tips; the boxes' own are the fallback.
  CLASS_TRAILS[i] ??= { nacelles: s.kind === "twin" ? s.nacZ : 1.8, tip: s.span / 2 };
  CLASS_MUZZLES[i] = s.muzzles;
  DAMAGE_ANCHORS[i] = hangarAnchors(s);
  const tl = s.tailGroup;
  const stripe = { x: tl.x - 0.45, y: 0.4 + (tl.finTop - 0.4) * 0.6, w: 0.5, h: 0.44, d: 0.34 };
  TAIL_STRIPE_ANCHORS[i] = tl.finZ ? [{ ...stripe, z: tl.finZ }, { ...stripe, z: -tl.finZ }] : [{ ...stripe, z: 0 }];
});

// A class's factory livery with the scheme's body/accent overrides applied.
// A scheme's body paints the wings with the fuselage, as it does on the model
// (whose wing uppers are the same camouflage material the scheme recolors).
// A livery's far silhouette: its flank, its uppers and its band (or cowl, or a
// Corsair's cowl ring). A
// camouflage it lays as decals (`Camo`, the Spitfire's Dark Green) covers about
// half of both, so past 2 km the two read as their mix.
function applyScheme(type, schemeDef) {
  const base = CLASS_LIVERY[type] ?? CLASS_LIVERY[0];
  if (!schemeFits(schemeDef, type)) return base;
  const t = { ...base };
  if (schemeDef.body) t.body = t.color = new THREE.Color(schemeDef.body).getHex();
  if (schemeDef.accent) t.accent = new THREE.Color(schemeDef.accent).getHex();
  const lp = schemeDef.paint;
  if (lp) {
    const camo = (hex) => (lp.Camo ? new THREE.Color(hex).lerp(new THREE.Color(lp.Camo), 0.5) : new THREE.Color(hex)).getHex();
    if (lp.Side) t.body = camo(lp.Side);
    if (lp.Upper) t.color = camo(lp.Upper);
    // The trim boxes (tip caps, cowl ring) wear the band or cowl; a Corsair
    // livery paints its old white ring and tips with the flank, and a
    // Lightning, with no cowl ring of its own, wears its spinners'.
    const trim = lp.Band ?? lp.Cowl ?? lp.Side_Cowl ?? lp.Spinner;
    if (trim) t.accent = new THREE.Color(trim).getHex();
  }
  return t;
}

// The box list for a scheme's fin stripe, or [] when the scheme paints none.
function tailStripeBoxes(type, schemeDef) {
  if (!schemeFits(schemeDef, type) || !schemeDef.tailStripe) return [];
  const color = new THREE.Color(schemeDef.tailStripe).getHex();
  const anchors = TAIL_STRIPE_ANCHORS[type] ?? [];
  return anchors.map((b) => ({ ...b, color }));
}

const planeGeoCache = new Map(); // `${type}:${scheme}` -> geometry

// Exported so the living-airbase parked-aircraft models (roadmap #43) can build
// a plane hull; the scheme arg defaults to 0 (factory) for those callers. No
// team: a Crimson and a Cobalt airframe are the same geometry.
export function planeGeometry(type, scheme = 0) {
  const key = `${type}:${scheme}`;
  if (planeGeoCache.has(key)) return planeGeoCache.get(key);
  const builder = CLASS_BUILDERS[type] ?? CLASS_BUILDERS[0];
  const def = SCHEMES[scheme];
  const boxes = builder(applyScheme(type, def)).concat(tailStripeBoxes(type, def));
  const geo = mergeBoxes(boxes);
  planeGeoCache.set(key, geo);
  return geo;
}

let propGeo = null;
function propellerGeometry() {
  if (propGeo) return propGeo;
  propGeo = mergeBoxes([
    { x: 0, y: 0, z: 0, w: 0.12, h: 3.4, d: 0.4, color: 0x17181c },
    { x: 0, y: 0, z: 0, w: 0.12, h: 0.4, d: 3.4, color: 0x17181c },
    // Safety-yellow blade tips, period style.
    { x: 0, y: 1.62, z: 0, w: 0.13, h: 0.34, d: 0.42, color: 0xf4c542 },
    { x: 0, y: -1.62, z: 0, w: 0.13, h: 0.34, d: 0.42, color: 0xf4c542 },
    { x: 0, y: 0, z: 1.62, w: 0.13, h: 0.42, d: 0.34, color: 0xf4c542 },
    { x: 0, y: 0, z: -1.62, w: 0.13, h: 0.42, d: 0.34, color: 0xf4c542 },
  ]);
  return propGeo;
}

// Faint spinning prop disc; opacity follows throttle in update().
let discGeo = null;
function propDiscGeometry() {
  if (discGeo) return discGeo;
  discGeo = new THREE.CylinderGeometry(1.78, 1.78, 0.06, 12);
  discGeo.rotateZ(Math.PI / 2); // axis along +X like the crankshaft
  return discGeo;
}

// Afterburner plume: a unit cone with its base at the origin (the nozzle)
// tapering to a point one unit aft along -X; PlaneVisual scales it with the
// throttle. Open-ended so there's no cap disc sitting across the nozzle.
let plumeGeo = null;
function afterburnerGeometry() {
  if (plumeGeo) return plumeGeo;
  plumeGeo = new THREE.ConeGeometry(0.5, 1, 12, 1, true);
  plumeGeo.translate(0, 0.5, 0); // base at y=0, apex at y=1
  plumeGeo.rotateZ(Math.PI / 2); // apex swings onto -X: base 0 .. tip -1
  return plumeGeo;
}

// One shock diamond: a unit octahedron, stretched and strung down the plume
// axis by PlaneVisual to fake the standing Mach-disc interference pattern a
// real afterburner paints inside its exhaust.
let diamondGeo = null;
function shockDiamondGeometry() {
  if (diamondGeo) return diamondGeo;
  diamondGeo = new THREE.OctahedronGeometry(1);
  return diamondGeo;
}

// Crew-turret sub-geometries (team-independent), cached by part key.
const turretGeoCache = new Map();
function turretGeometry(key, build) {
  let geo = turretGeoCache.get(key);
  if (!geo) {
    geo = mergeBoxes(build());
    turretGeoCache.set(key, geo);
  }
  return geo;
}
const planeMaterial = new THREE.MeshLambertMaterial({ vertexColors: true });
// Battle-damage overlay shares the lit, vertex-colored look of the airframe but
// is pulled slightly toward the camera so scorch/holes win the depth test
// against the body panels they sit on instead of z-fighting them.
const damageMaterial = new THREE.MeshLambertMaterial({
  vertexColors: true,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  polygonOffsetUnits: -1,
});
// The box airframes' paints, for tier 2's sheen (gfx2_light.js) to find.
export const PLANE_PAINTS = [planeMaterial, damageMaterial];
// The guns' muzzle flashes are flipbook flash cards (flipbooks.js): a lance of
// flame off each barrel that reaches about half its frame past the muzzle,
// never the same twice. A wing gun's frame (its lance about as long as the
// old flash box, 1.3 m), and a turret's lighter twin guns'.
const GUN_FLASH_M = 2.6;
const TURRET_FLASH_M = 2.0;
// The own gun flash's size seen from the cockpit (PlaneVisual._applyInterior).
const FLASH_SEAT_SCALE = 0.2;

// Banner cloth for the ad circus (CLASS_BANNERS): the message drawn onto a
// canvas texture, sized to fit however long the sponsor rambles. Two looks:
// "towed" is white cloth with poster-red letters and red edge tapes (the
// classic beach-plane banner); "sides" is the zeppelin's framed hull panel.
function makeBannerTexture(text, kind) {
  const canvas = document.createElement("canvas");
  // Aspect ratios match the meshes they map onto (see _buildBanner): the
  // towed cloth is a long ribbon, the hull panel a squatter board.
  canvas.width = 2048;
  canvas.height = kind === "sides" ? 512 : 320;
  const W = canvas.width, H = canvas.height;
  const ctx = canvas.getContext("2d");
  const msg = (text || "").toUpperCase();
  if (kind === "sides") {
    ctx.fillStyle = "#f2ead2"; // painted panel on the gasbag
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "#c8452e";
    ctx.lineWidth = 24;
    ctx.strokeRect(18, 18, W - 36, H - 36);
    ctx.fillStyle = "#1d2a4a";
  } else {
    ctx.fillStyle = "#f5f0e0"; // towed cloth
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#c8452e";
    ctx.fillRect(0, 0, W, 28); // edge tapes
    ctx.fillRect(0, H - 28, W, 28);
  }
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Shrink to fit: long copy gets smaller letters, not a clipped slogan.
  let size = Math.floor(H * 0.56);
  do {
    ctx.font = `bold ${size}px 'Courier New', monospace`;
    size -= 8;
  } while (size > 40 && ctx.measureText(msg).width > W - 120);
  ctx.fillText(msg, W / 2, H / 2 + 4);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

// --- the Marquee's sign board ----------------------------------------------
// The ad zeppelin doesn't just paint its copy on the gasbag — it hangs a proper
// theater marquee off each flank: a timber board, the poster, a gold rim, and a
// ring of running bulbs that chase around the frame the way they do over a
// picture house on opening night. Everything below shares one rectangle so the
// board, the poster and the lights can never drift out of register.
const MQ_CY = 0.3; // sign center, level with the hull's flat middle section
const MQ_HALF_W = 5.55; // bulb ring half-width  (the poster's is 5.25)
const MQ_HALF_H = 1.55; // bulb ring half-height (the poster's is 1.3)
const MQ_BOARD_Z = 3.02; // board center: sunk into the gasbag, proud at the corners
const MQ_BOARD_D = 0.44;
const MQ_FACE_Z = MQ_BOARD_Z + MQ_BOARD_D / 2; // the board's outer face
// A filament bulb is warm-white when it's on and dead brass when it isn't —
// the dark bulbs are what make the lit ones read as a chase and not a stripe.
const MQ_LIT = new THREE.Color(0xfff3c8);
const MQ_DIM = new THREE.Color(0x5c4b30);
const MQ_HALO = new THREE.Color(0xffcf7a);
const MQ_OFF = new THREE.Color(0x000000); // additive black: an unlit halo costs nothing
const MQ_CHASE_HZ = 7.5; // bulbs per second the running light travels
const MQ_CHASE_GAP = 3; // one bulb in three burns at any instant

// Timber board, gold rim beading, one per flank. Merged into a single mesh so
// the whole sign is one draw call on top of the hull's.
function marqueeBoardBoxes() {
  const boxes = [];
  for (const s of [1, -1]) {
    const z = s * MQ_BOARD_Z;
    const face = s * (MQ_FACE_Z + 0.05);
    boxes.push(
      { x: 0, y: MQ_CY, z, w: 11.9, h: 3.8, d: MQ_BOARD_D, color: 0x2a2320 },
      // Beading right at the edge, outboard of the bulbs: the frame still reads
      // as a marquee in daylight, before you notice the lights running.
      { x: 0, y: MQ_CY + 1.78, z: face, w: 11.9, h: 0.24, d: 0.1, color: circusGold },
      { x: 0, y: MQ_CY - 1.78, z: face, w: 11.9, h: 0.24, d: 0.1, color: circusGold },
      { x: 5.83, y: MQ_CY, z: face, w: 0.24, h: 3.8, d: 0.1, color: circusGold },
      { x: -5.83, y: MQ_CY, z: face, w: 0.24, h: 3.8, d: 0.1, color: circusGold },
    );
  }
  return boxes;
}

// Bulb sockets walked once around the frame — top rail left-to-right, down the
// right post, bottom rail back, up the left post. The ORDER is the animation:
// stepping the lit set by index is what sends the chase around the loop, so
// never sort these or the running light starts hopping corners.
function marqueeRing() {
  const pts = [];
  const nx = 19, ny = 4; // rail bulbs / post bulbs between the corners
  const spanX = 2 * MQ_HALF_W, spanY = 2 * MQ_HALF_H;
  for (let i = 0; i < nx; i++) pts.push([-MQ_HALF_W + (spanX * i) / (nx - 1), MQ_CY + MQ_HALF_H]);
  for (let i = 1; i <= ny; i++) pts.push([MQ_HALF_W, MQ_CY + MQ_HALF_H - (spanY * i) / (ny + 1)]);
  for (let i = 0; i < nx; i++) pts.push([MQ_HALF_W - (spanX * i) / (nx - 1), MQ_CY - MQ_HALF_H]);
  for (let i = 1; i <= ny; i++) pts.push([-MQ_HALF_W, MQ_CY - MQ_HALF_H + (spanY * i) / (ny + 1)]);
  return pts;
}

// Past this range a nameplate stops shrinking and holds the size it had here
// (PlaneVisual.update). No airframe wears a team color, so the tile is the one
// place a plane shows its side, and it has to read wherever the wings used to:
// 350 m is the last range its 14 m tile is a solid chip rather than a sliver,
// and from there out it stays that chip over a speck of an airplane.
const NAMEPLATE_TRUE_M = 350;

const cssHex = (hex) => `#${hex.toString(16).padStart(6, "0")}`;

// Nameplate badge: the unit insignia (insignia.js) rides the left end of the
// callsign bar. A long callsign first steps its type down to keep the tile at
// its 14 m chip (PLATE_FIT_PX of lettering beside the badge's slot), but never
// below PLATE_MIN_PX, where bold Courier stops reading at range. Past that
// floor the TILE grows instead, and the canvas and the sprite grow with it at
// the same pixels-per-metre: "Duchess Divebomb II" behind a badge once ran
// her D over the pig and her II off the Cobalt chip.
const PLATE_BADGE_PX = 44;
const PLATE_BADGE_SLOT = PLATE_BADGE_PX + 8;
const PLATE_FIT_PX = 226;
const PLATE_MIN_PX = 20;
const plateFont = (px) => `bold ${px}px 'Courier New', monospace`;

// A pilot's floating callsign, flown on a tile in the team color: a Crimson or
// Cobalt chip you can read before you can read the name. Your nemesis's tile
// wears an orange frame for you and you alone (roadmap #21); the teamless ad
// circus (team 2, adfleet.go) keeps a dark tile and signs in circus gold.
export function makeNameplate(name, team, nemesis = false, title = "", ins = "", onBadge = null) {
  const titled = !!title;
  const side = TEAM_INFO[team] ?? null;
  // A badge the catalog carries gets its slot at once, drawn or not, so the
  // plate doesn't jump sideways when the image lands a moment later.
  const badged = !!insigniaURL(ins);
  const badge = badged ? insigniaImage(ins, onBadge) : null;
  const slot = badged ? PLATE_BADGE_SLOT : 0;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  // Callsign row. Size the type first, then the tile around it: the chip is
  // the side, so the name must sit inside it, 12 px clear of each end and of
  // the badge — never spilling off an edge or over the insignia.
  let px = 34;
  ctx.font = plateFont(px);
  const tw0 = ctx.measureText(name).width;
  const fitW = PLATE_FIT_PX - slot;
  if (tw0 > fitW) px = Math.max(PLATE_MIN_PX, Math.floor((px * fitW) / tw0));
  ctx.font = plateFont(px);
  const w = ctx.measureText(name).width + 24 + slot; // the tile
  canvas.width = Math.max(256, Math.ceil(w) + 6);
  canvas.height = titled ? 84 : 56;
  const cx = canvas.width / 2;
  ctx.font = plateFont(px); // (resizing the canvas reset the context)
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  if (side) {
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = side.cssColor;
    ctx.fillRect(cx - w / 2, 4, w, 48);
    ctx.globalAlpha = 1;
    // A darker rim keeps the tile's edge against a bright sky or a cloud top.
    const rim = nemesis ? 6 : 3;
    ctx.lineWidth = rim;
    ctx.strokeStyle = nemesis ? "#ff9a3a" : cssHex(side.body);
    ctx.strokeRect(cx - w / 2 + rim / 2, 4 + rim / 2, w - rim, 48 - rim);
    ctx.shadowColor = "rgba(0,0,0,0.55)";
    ctx.shadowOffsetY = 2;
    ctx.shadowBlur = 2;
    ctx.fillStyle = "#fff8ec";
  } else {
    ctx.fillStyle = "rgba(0,0,0,0.45)";
    ctx.fillRect(cx - w / 2, 4, w, 48);
    ctx.fillStyle = "#f4c542";
  }
  if (badge) ctx.drawImage(badge, cx - w / 2 + 6, 6, PLATE_BADGE_PX, PLATE_BADGE_PX);
  ctx.fillText(name, cx + slot / 2, 28);
  ctx.shadowColor = "transparent";
  // Career title row on a darker team tile under the callsign (roadmap #26).
  if (titled) {
    ctx.font = "bold 22px 'Courier New', monospace";
    const tw = Math.min(252, ctx.measureText(title).width + 20);
    ctx.globalAlpha = side ? 0.9 : 0.4;
    ctx.fillStyle = side ? cssHex(side.body) : "#000";
    ctx.fillRect(cx + slot / 2 - tw / 2, 55, tw, 26);
    ctx.globalAlpha = 1;
    ctx.fillStyle = side ? "#f2e3c8" : "#c8a84a";
    ctx.fillText(title, cx + slot / 2, 69);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: tex, depthTest: true, transparent: true }),
  );
  const base = { w: (14 * canvas.width) / 256, h: titled ? 4.6 : 3.1, y: titled ? 4.4 : 4 };
  sprite.userData.base = base;
  sprite.scale.set(base.w, base.h, 1);
  sprite.position.set(0, base.y, 0);
  return sprite;
}

// Ace markers (roadmap #26): a gold spinner cap and a small fin pennant, both
// cached and cheap, and neither in a team color — nothing on an airframe is.
// Geometry only — no emissive, so they never light up as a night targeting
// beacon.
let aceSpinnerGeo = null;
function aceSpinner() {
  if (!aceSpinnerGeo) {
    aceSpinnerGeo = mergeBoxes([
      { x: 0, y: 0, z: 0, w: 0.5, h: 0.62, d: 0.62, color: 0xd8b84a },
      { x: 0.32, y: 0, z: 0, w: 0.34, h: 0.34, d: 0.34, color: 0xe8cf78 },
    ]);
  }
  return aceSpinnerGeo;
}
let acePennantGeo = null;
function acePennant() {
  if (!acePennantGeo) {
    acePennantGeo = mergeBoxes([
      { x: 0, y: 0, z: 0, w: 0.12, h: 0.42, d: 0.5, color: 0xd8b84a }, // gold staff
      { x: -0.55, y: 0.12, z: 0, w: 1.0, h: 0.34, d: 0.06, color: 0xf2e3c8 }, // white silk
      { x: -0.9, y: 0.12, z: 0, w: 0.3, h: 0.36, d: 0.07, color: 0xd8b84a }, // gold fly end
    ]);
  }
  return acePennantGeo;
}

// --- losing whole sections (advanced damage) --------------------------------
// The body is one merged mesh and the wing is a single full-span slab, so a
// section can't just be hidden. Instead we build a one-off instance body with
// the lost boxes trimmed/dropped, and shed the piece into the world.

// Trim every box so nothing on side `s` (+1 right / -1 left) reaches past the
// shear line; boxes wholly outboard of it are dropped. Returns a new list.
function clipWing(boxes, type, s) {
  const breakAt = wingBreakAt(type);
  const out = [];
  for (const b of boxes) {
    const hd = (b.cone ? Math.max(b.r0 ?? 0, b.r1 ?? 0) : b.d / 2);
    const u0 = s * b.z; // piece centre projected onto the cut axis
    const inner = u0 - hd;
    const outer = u0 + hd;
    if (inner >= breakAt) continue; // wholly outboard — sheared away
    if (outer > breakAt) {
      if (b.cone) continue; // a cone can't be trimmed: the whole pod shears off
      const uNew = (inner + breakAt) / 2;
      out.push({ ...b, d: breakAt - inner, z: s * uNew });
    } else {
      out.push(b);
    }
  }
  return out;
}

// Shear the whole empennage off: drop every flying surface aft of the break
// (the wide tailplane slabs and anything up the fin), keeping the boom, the
// tailwheel and — on the jet — the burner can, so the wreck still reads as
// a fuselage that lost its feathers.
function clipTail(boxes, type) {
  const tl = (DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0]).tail;
  const cut = tl.x + 0.55;
  return boxes.filter((b) => {
    if (b.x >= cut) return true;
    const zReach = Math.abs(b.z ?? 0) + (b.d ?? 0) / 2;
    return !(b.y > 0.75 || zReach > 1.0); // flying surfaces only
  });
}

// Drop the canopy glass near the cockpit and leave a dark, open tub.
function stripCanopy(boxes, type) {
  const cp = (DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0]).canopy;
  const out = boxes.filter(
    (b) => !((b.color === glass || b.color === glassDeep) && Math.abs(b.x - cp.x) < cp.w * 0.85 && b.y > 0.45),
  );
  out.push({ x: cp.x, y: cp.y - 0.18, z: 0, w: cp.w * 0.66, h: 0.36, d: cp.d * 0.66, color: 0x0b0d0f });
  return out;
}

// Instance body geometry with the lost sections sheared off (cached per combo).
function damagedBody(type, lost, scheme = 0) {
  const key = `${type}:${scheme}:x${lost.wingL ? "L" : ""}${lost.wingR ? "R" : ""}${lost.canopy ? "C" : ""}${lost.tail ? "T" : ""}`;
  const hit = planeGeoCache.get(key);
  if (hit) return hit;
  const def = SCHEMES[scheme];
  let boxes = (CLASS_BUILDERS[type] ?? CLASS_BUILDERS[0])(applyScheme(type, def)).concat(tailStripeBoxes(type, def));
  if (lost.wingR) boxes = clipWing(boxes, type, 1);
  if (lost.wingL) boxes = clipWing(boxes, type, -1);
  if (lost.canopy) boxes = stripCanopy(boxes, type);
  if (lost.tail) boxes = clipTail(boxes, type);
  const geo = mergeBoxes(boxes);
  planeGeoCache.set(key, geo);
  return geo;
}

// Geometry for the torn-off pieces, centred on their own origin so they tumble,
// in the paint of the airframe they came off.
function wingtipDebrisGeo(type, scheme = 0) {
  const key = `${type}:${scheme}:wtd`;
  const hit = planeGeoCache.get(key);
  if (hit) return hit;
  const w = (DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0]).wing;
  const t = applyScheme(type, SCHEMES[scheme]);
  const len = w.tipZ * (1 - WING_KEEP);
  const geo = mergeBoxes([
    { x: w.x, y: w.y, z: 0, w: w.chord * 1.8, h: w.thick, d: len, color: t.color },
    { x: w.x, y: w.y + 0.05, z: 0, w: w.chord, h: 0.1, d: w.chord, color: t.accent },
    { x: w.x, y: w.y, z: len * 0.42, w: w.chord * 1.4, h: w.thick * 1.05, d: 0.5, color: t.accent },
    { x: w.x - w.chord * 0.7, y: w.y, z: -len * 0.45, w: 0.5, h: w.thick, d: 0.5, color: charBlack },
  ]);
  planeGeoCache.set(key, geo);
  return geo;
}

// The torn-off empennage: tailplane cross with a fin stub, tumbling aft.
function tailDebrisGeo(type, scheme = 0) {
  const key = `${type}:${scheme}:tld`;
  const hit = planeGeoCache.get(key);
  if (hit) return hit;
  const tl = (DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0]).tail;
  const t = applyScheme(type, SCHEMES[scheme]);
  const finH = Math.max(0.8, (tl.finY - tl.y) * 0.9);
  const geo = mergeBoxes([
    { x: 0, y: 0, z: 0, w: 1.3, h: 0.24, d: tl.spanZ * 2, color: t.color }, // tailplane
    { x: -0.45, y: 0, z: 0, w: 0.4, h: 0.2, d: tl.spanZ * 1.8, color: shade(t.color, 0.78) }, // elevator
    { x: -0.15, y: finH * 0.5, z: 0, w: 0.9, h: finH, d: 0.26, color: t.body }, // fin stub
    { x: 0.55, y: 0.05, z: 0, w: 0.4, h: 0.5, d: 0.5, color: charBlack }, // charred break
  ]);
  planeGeoCache.set(key, geo);
  return geo;
}

function canopyDebrisGeo(type, scheme = 0) {
  const key = `${type}:${scheme}:cpd`;
  const hit = planeGeoCache.get(key);
  if (hit) return hit;
  const cp = (DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0]).canopy;
  const t = applyScheme(type, SCHEMES[scheme]);
  const geo = mergeBoxes([
    { x: 0, y: 0, z: 0, w: cp.w * 0.66, h: 0.36, d: cp.d * 0.66, color: glass },
    { x: 0, y: 0.24, z: 0, w: cp.w * 0.46, h: 0.12, d: cp.d * 0.46, color: glassDeep },
    // A dark frame ring + charred sill so the torn-off hood reads against sky.
    { x: cp.w * 0.34, y: 0, z: 0, w: 0.18, h: 0.46, d: cp.d * 0.78, color: t.body },
    { x: -cp.w * 0.34, y: 0, z: 0, w: 0.18, h: 0.46, d: cp.d * 0.78, color: t.body },
    { x: 0, y: -0.22, z: 0, w: cp.w * 0.74, h: 0.16, d: cp.d * 0.74, color: charBlack },
  ]);
  planeGeoCache.set(key, geo);
  return geo;
}

// World-space tumbling wreckage shed when a section tears off. A piece is
// either a box-era debris geometry or a model rig's torn section (an Object3D
// already posed in the world — see AircraftRig.debrisFor).
const _debris = [];
const _gravity = new THREE.Vector3(0, -9.81, 0);

function shedDebris(scene, piece, pos, quat, vel) {
  let mesh = piece;
  if (piece.isBufferGeometry) {
    mesh = new THREE.Mesh(piece, planeMaterial);
    mesh.position.copy(pos);
    mesh.quaternion.copy(quat);
  }
  scene.add(mesh);
  _debris.push({
    scene,
    mesh,
    vel: vel.clone(),
    spin: new THREE.Vector3((Math.random() - 0.5) * 7, (Math.random() - 0.5) * 4, (Math.random() - 0.5) * 9),
    life: 0,
  });
}

/** Advance and retire shed wreckage; call once per frame from the render loop. */
export function updateDebris(dt) {
  for (let i = _debris.length - 1; i >= 0; i--) {
    const d = _debris[i];
    d.life += dt;
    d.vel.addScaledVector(_gravity, dt);
    d.mesh.position.addScaledVector(d.vel, dt);
    d.mesh.rotation.x += d.spin.x * dt;
    d.mesh.rotation.y += d.spin.y * dt;
    d.mesh.rotation.z += d.spin.z * dt;
    if (d.life >= 4.5) {
      d.scene.remove(d.mesh);
      _debris.splice(i, 1);
    }
  }
}

// The box-era piece for a torn-off section: "canopy", "tail", or a wing.
function sectionDebrisGeo(type, kind, scheme = 0) {
  return kind === "canopy" ? canopyDebrisGeo(type, scheme)
    : kind === "tail" ? tailDebrisGeo(type, scheme)
    : wingtipDebrisGeo(type, scheme);
}

/** A pre-built tumbling section piece for the screenshot director (no physics). */
export function makeSectionDebris(type, kind, scheme = 0) {
  return new THREE.Mesh(sectionDebrisGeo(type, kind, scheme), planeMaterial);
}

/** Local-space anchor where a section shears (for placing debris). */
export function sectionBreakPoint(type, kind) {
  const a = DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0];
  if (kind === "canopy") return new THREE.Vector3(a.canopy.x, a.canopy.y, 0);
  if (kind === "tail") return new THREE.Vector3(a.tail.x, a.tail.y, 0);
  const s = kind === "wingL" ? -1 : 1;
  return new THREE.Vector3(a.wing.x, a.wing.y, s * wingBreakAt(type));
}

// A scheme as the model painter wants it ({ body, accent } hex strings, and a
// livery's per-marking `paint` and decal set), or null for the factory finish
// — which is also what a livery is on an airframe it wasn't painted for.
function schemePaint(def, type) {
  if (!schemeFits(def, type) || (!def.body && !def.accent && !def.paint)) return null;
  const out = { body: def.body ?? null, accent: def.accent ?? null };
  if (def.paint) Object.assign(out, { key: def.id, paint: def.paint, livery: def.livery ?? null });
  return out;
}

// The model damage thresholds, handed to the rig (it never imports this file).
const RIG_LIMITS = { WING_LOSE, TAIL_LOSE, CANOPY_LOSE };

/**
 * A renderable plane. The airframe is a THREE.LOD: the art/ model at three
 * levels of detail up close (model-integration.md Part 3), and past 2 km — or
 * before the model has loaded, or with ?boxes — the blocky box silhouette with
 * its own props, turrets and damage overlay, exactly as the game always drew
 * it. Everything that isn't airframe (muzzle flashes, prop discs, burner
 * plume, nameplate, banner, career markers) hangs off `group` and follows
 * whichever airframe is showing.
 */
export class PlaneVisual {
  constructor(scene, team, name, isSelf, type = 0, scheme = 0, ad = "", ins = "") {
    this.scene = scene;
    this.team = team;
    this.name = name;
    this.isSelf = isSelf;
    this.scheme = scheme | 0; // equipped paint scheme index (roadmap #25)
    this.title = "";          // career title on the nameplate (roadmap #26)
    this.ins = ins || "";     // unit insignia at the plate's left end (insignia.js)
    this._nemesis = false;    // orange callsign for the local viewer (roadmap #21)
    this._ace = false;        // gold spinner + fin pennant (roadmap #26)
    this.ad = ad || "";       // banner copy for the ad circus (adfleet.go)
    this._bannerT = Math.random() * 10; // desync the cloth flutter per craft
    this.group = new THREE.Group();
    scene.add(this.group);
    // Seeds the deterministic bullet-hole scatter (roadmap #8); set by main.js
    // to the snapshot entity id so every client pocks the airframe identically.
    this.entityId = 0;
    this.rig = null;
    this.buildType(type);
    this.throttle = 0.7;
  }

  buildType(type) {
    this.type = type;
    this.rig?.dispose();
    this.rig = null;
    this._rigToken = null;
    this.group.clear(); // also drops the old damage overlay
    this.damage = null;
    this._dmgSig = null;
    this._lastDmg = null;
    this._lostKey = undefined; // no sections lost on a fresh airframe
    this.killMarks = null; // rebuilt on demand by setKillMarks
    this._killN = -1;
    this.discMat?.dispose();
    this.stripe = null;

    // The box silhouette: its own level of the LOD, carrying the box props,
    // box turrets and box damage overlay so none of them show over a model.
    this.lod = new ModelLOD();
    this.boxLevel = new THREE.Group();
    this.body = new THREE.Mesh(planeGeometry(type, this.scheme), planeMaterial);
    this.boxLevel.add(this.body);
    setLodLevels(this.lod, [[this.boxLevel, 0]]);
    this.group.add(this.lod);

    this.discMat = new THREE.MeshBasicMaterial({
      color: 0x33363d,
      transparent: true,
      opacity: 0.18,
      depthWrite: false,
    });
    this.props = [];
    for (const [px, pz] of CLASS_PROPS[type] ?? CLASS_PROPS[0]) {
      const prop = new THREE.Mesh(propellerGeometry(), planeMaterial);
      prop.position.set(px, 0, pz);
      this.props.push(prop);
      this.boxLevel.add(prop);
    }

    this.jetMat?.dispose();
    this.jetCoreMat?.dispose();
    this.jetMat = null;
    this.jetCoreMat = null;

    // Crewable defensive turrets (bombers only): a yaw ring carrying a
    // pitching twin-gun cradle, eased toward the aim the snapshots (or the
    // local gunner's prediction) report, stowed when the seat is empty.
    this.turrets = [];
    for (const spec of CLASS_TURRETS[type] ?? []) {
      const yawPivot = new THREE.Group();
      yawPivot.position.set(spec.pos[0], spec.pos[1], spec.pos[2]);
      const dome = new THREE.Mesh(turretGeometry(`${spec.kind}:dome`, () => turretDomeBoxes(spec.kind)), planeMaterial);
      yawPivot.add(dome);
      const pitchPivot = new THREE.Group();
      yawPivot.add(pitchPivot);
      const guns = new THREE.Mesh(
        turretGeometry(`${spec.kind}:guns:${spec.barrelLen}`, () => turretGunBoxes(spec.kind, spec.barrelLen)),
        planeMaterial,
      );
      pitchPivot.add(guns);
      const gy = spec.kind === "top" ? 0.12 : 0.05;
      const flashes = [];
      for (const z of [0.24, -0.24]) {
        const flash = muzzleFlash(TURRET_FLASH_M);
        flash.position.set(spec.barrelLen, gy, z);
        flashes.push(flash);
        pitchPivot.add(flash);
      }
      const tur = {
        spec, yawPivot, pitchPivot, flashes,
        yaw: spec.stowYaw, pitch: spec.stowPitch,
        tgtYaw: spec.stowYaw, tgtPitch: spec.stowPitch,
        manned: false, firing: false,
        rigAim: null, // the model's flash pivot, once a rig is mounted
      };
      yawPivot.rotation.y = tur.yaw;
      pitchPivot.rotation.z = tur.pitch;
      this.turrets.push(tur);
      this.boxLevel.add(yawPivot);
    }

    // Prop discs, muzzle flashes and burner plumes for the box airframe; a
    // model rebuilds them on its own hubs, barrels and nozzles.
    this.fittings = new THREE.Group();
    this.group.add(this.fittings);
    this._buildFittings();

    this.nameplate = null;
    if (!this.isSelf) {
      this.nameplate = this._makeNameplate();
      this.group.add(this.nameplate);
    }
    // The circus hangs its message (a fresh rebuild wipes the old cloth).
    this.banner = null;
    this._buildBanner();
    // Re-attach the ace markers after a full rebuild (a plane change wipes them).
    this._aceMeshes = null;
    if (this._ace) this._buildAce();

    this._requestRig(type);
  }

  // Muzzle flashes, prop blur discs and jet plumes, placed for whichever
  // airframe is mounted. Rebuilt (cheaply) when a model arrives.
  _buildFittings() {
    const type = this.type;
    const rig = this.rig;
    this.fittings.clear();

    this.discs = [];
    const discs = rig
      ? rig.propDiscs.map((p) => ({ x: p.pos.x + Math.min(0.3, p.hub * 0.4), y: p.pos.y, z: p.pos.z, r: p.r }))
      : (CLASS_PROPS[type] ?? CLASS_PROPS[0]).map(([px, pz]) => ({ x: px + 0.1, y: 0, z: pz, r: 1.78 }));
    for (const d of discs) {
      const disc = new THREE.Mesh(propDiscGeometry(), this.discMat);
      disc.position.set(d.x, d.y, d.z);
      disc.scale.setScalar(d.r / 1.78);
      this.discs.push(disc);
      this.fittings.add(disc);
    }

    // One flash per barrel the class's battery fires. A real battery's list
    // is its barrel tips (applyRoster); a legacy one lays the model's barrels
    // over its count, trimmed or doubled up, so the sight picture is the
    // stream the server is throwing.
    const box = CLASS_MUZZLES[type] ?? CLASS_MUZZLES[0];
    let muzzles = box;
    if (rig && rig.spec.muzzles.length && !EXACT_MUZZLES.has(type)) {
      muzzles = [];
      for (let i = 0; i < box.length; i++) muzzles.push(rig.spec.muzzles[i % rig.spec.muzzles.length]);
    }
    this.flashes = [];
    for (const [mx, my, mz] of muzzles) {
      const flash = muzzleFlash(GUN_FLASH_M);
      flash.position.set(mx, my, mz);
      flash.userData.size = GUN_FLASH_M; // what the seat scales it from (_applyInterior)
      this.flashes.push(flash);
      this.fittings.add(flash);
    }

    // Jet exhaust: an additive afterburner cone off each tailpipe whose
    // length and glow ride the throttle (jets carry no propeller), with a
    // string of white-hot shock diamonds standing inside the plume.
    this.jets = [];
    const pipes = rig
      ? (rig.spec.nozzles ?? [])
      : CLASS_JETPIPES[type] ? [CLASS_JETPIPES[type].pos] : [];
    if (pipes.length && !this.jetMat) {
      // Normal (not additive) blending: additive washes to white against a
      // bright sky and the diamonds vanish; a translucent orange cone with
      // near-opaque cream diamonds drawn over it reads in any light.
      this.jetMat = new THREE.MeshBasicMaterial({
        color: 0xff8a2e,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        fog: false,
      });
      this.jetCoreMat = new THREE.MeshBasicMaterial({
        color: 0xffe9b0,
        transparent: true,
        opacity: 0.95,
        depthWrite: false,
        fog: false,
      });
    }
    for (const pos of pipes) {
      // Unit-space plume group (base at the nozzle, tip 1 unit aft): update()
      // stretches the whole group, so the diamonds ride the flame's length.
      const jet = new THREE.Group();
      const plume = new THREE.Mesh(afterburnerGeometry(), this.jetMat);
      plume.renderOrder = 1;
      jet.add(plume);
      jet.userData.diamonds = [];
      const N = 6;
      for (let i = 0; i < N; i++) {
        const f = (i + 0.55) / (N + 0.6); // fraction down the plume
        const d = new THREE.Mesh(shockDiamondGeometry(), this.jetCoreMat);
        d.position.x = -f;
        const r = 0.38 * (1 - f * 0.8); // diamonds shrink toward the tip
        d.scale.set(0.085, r, r);
        d.renderOrder = 2; // always over the plume cone they stand inside
        jet.userData.diamonds.push(d);
        jet.add(d);
      }
      jet.position.set(pos[0], pos[1], pos[2]);
      this.jets.push(jet);
      this.fittings.add(jet);
    }
    // Back-compat for callers that poke the single plume.
    this.jet = this.jets[0] ?? null;

    // Turret muzzle flashes for a model: a flash pivot at each crew turret's
    // pivot that turns with the TRUE aim (the dome itself only tilts a little).
    for (const t of this.turrets) t.rigAim = null;
    if (rig) {
      rig.spec.turrets?.forEach((ts, seat) => {
        const tur = this.turrets[seat];
        const node = rig.turrets[0]?.[seat]?.node;
        if (!tur || !node) return;
        const yawPivot = new THREE.Group();
        yawPivot.position.copy(node.position);
        const pitchPivot = new THREE.Group();
        yawPivot.add(pitchPivot);
        const flashes = [];
        // A twin mount flashes both barrels; a lone gun (tip on the
        // centerline: the Bola guns, the A-20's tunnel fifty) just the one.
        for (const zs of ts.tip[2] ? [1, -1] : [1]) {
          const flash = muzzleFlash(TURRET_FLASH_M);
          flash.position.set(Math.abs(ts.tip[0]), ts.tip[1], zs * ts.tip[2]);
          flashes.push(flash);
          pitchPivot.add(flash);
        }
        this.fittings.add(yawPivot);
        tur.rigAim = { yawPivot, pitchPivot, flashes };
      });
    }
  }

  // Fetch this class's model (all three levels) and mount it when it lands. A
  // cached model mounts synchronously, so a respawn never flashes boxes.
  _requestRig(type) {
    const spec = modelForClass(type, CLASS_MODELS);
    if (!spec) return;
    const token = (this._rigToken = {});
    const stems = [spec.stem, `${spec.stem}_lod1`, `${spec.stem}_lod2`];
    const ready = stems.map((s) => modelIfReady(s));
    if (ready.every(Boolean)) {
      this._attachRig(spec, ready);
      return;
    }
    Promise.allSettled(stems.map((s) => loadModel(s))).then((res) => {
      if (token !== this._rigToken || this._disposed) return;
      const tpls = res.map((r) => (r.status === "fulfilled" ? r.value : null));
      if (!tpls[0]) return; // no LOD0: the boxes carry on
      this._attachRig(spec, tpls);
    });
  }

  _attachRig(spec, tpls) {
    const def = SCHEMES[this.scheme];
    this.rig = new AircraftRig(spec, tpls, { team: this.team, scheme: schemePaint(def, this.type) }, RIG_LIMITS);
    this.rig.onKit = () => this._reapplyDamage();
    setLodLevels(this.lod, this.rig.lodLevels(this.boxLevel));
    this._buildFittings();
    this._buildStripe();
    // Markers anchored to the airframe move to the model's own skin.
    if (this._killN > 0) {
      const n = this._killN;
      this._killN = -1;
      this.setKillMarks(n);
    }
    if (this._ace) {
      for (const m of this._aceMeshes ?? []) this.group.remove(m);
      this._buildAce();
    }
    if (this.banner) this._placeBanner();
    this._reapplyDamage();
    // A seat taken before the model landed (cockpit.js): the canopy and the
    // blades it hid were the boxes'; hide the model's too.
    this._applyInterior();
    // Our own airframe shows its wounds up close from the first hit.
    if (this.isSelf) this.rig.requestKit().then(() => this._reapplyDamage());
  }

  // A scheme's fin stripe on the model (the box stripe is baked in its body).
  _buildStripe() {
    if (this.stripe) {
      this.stripe.parent?.remove(this.stripe);
      this.stripe.geometry.dispose();
      this.stripe = null;
    }
    const def = SCHEMES[this.scheme];
    if (!this.rig || !schemeFits(def, this.type) || !def.tailStripe) return;
    const boxes = finStripeBoxes(this.rig.anchors, new THREE.Color(def.tailStripe).getHex());
    if (!boxes.length) return;
    this.stripe = new THREE.Mesh(mergeBoxes(boxes), damageMaterial);
    // On the LOD0 and LOD1 airframes: at LOD2 range a stripe is a pixel.
    this.rig.levels[0].base.root.add(this.stripe);
  }

  _reapplyDamage() {
    if (!this._lastDmg) return;
    this._dmgSig = null;
    this.applyDamage(this._lastDmg);
  }

  /**
   * First person (cockpit.js): hide the airframe's parts an office stands in
   * for — the canopy's glass and frame, a pilot figure — on the level a
   * camera in the seat sees (LOD0, damage kit included). `re` matches node
   * names with the prefix stripped; null puts them back, minus whatever the
   * damage has shattered or blown away. The rest of the airframe stays: the
   * wings, booms and engines out of the canopy are the outside model's.
   */
  setInterior(re) {
    const was = this.interior ?? null;
    this.interior = re ?? null;
    if (was && !this.interior) {
      for (const n of this._interiorNodes(was)) n.visible = true;
      this._reapplyDamage();
    }
    this._applyInterior();
  }

  _interiorNodes(re) {
    const lvl = this.rig?.levels[0];
    if (!lvl) return [];
    return [...lvl.base.nodes(re), ...(lvl.kit?.nodes(re) ?? [])];
  }

  _applyInterior() {
    // From the seat a gun's flash is a flicker at the wing's leading edge, not
    // the lance that has to read from the chase boom twenty metres back. (The
    // card's origin is its muzzle, so it shrinks back onto the barrel.)
    const k = this.interior ? FLASH_SEAT_SCALE : 1;
    for (const f of this.flashes ?? []) f.scale.setScalar(k * (f.userData.size ?? 1));
    if (!this.interior) return;
    for (const n of this._interiorNodes(this.interior)) n.visible = false;
  }

  /** The anchors the airframe-mounted markers use (model or box). */
  _anchors() {
    return this.rig?.anchors ?? DAMAGE_ANCHORS[this.type] ?? DAMAGE_ANCHORS[0];
  }

  /** Swap the banner copy in place (the "ad" event at the round bell). */
  setBanner(text) {
    text = text || "";
    if (this.ad === text) return;
    this.ad = text;
    this._buildBanner();
  }

  // Build (or rebuild) the ad banner for this type: a towed cloth trailing
  // the Skywriter on a rope, or a painted panel on each of the Marquee's
  // flanks. Non-circus types, or an empty message, fly clean.
  _buildBanner() {
    if (this.banner) {
      this.group.remove(this.banner);
      this.banner.traverse((o) => {
        o.geometry?.dispose?.();
        if (o.material) {
          o.material.map?.dispose();
          o.material.dispose();
        }
      });
      this.banner = null;
    }
    this._mq = null;
    const spec = CLASS_BANNERS[this.type];
    if (!spec || !this.ad) return;
    this.banner = new THREE.Group();
    if (spec.kind === "towed") {
      // Rope from the tail hook, then the cloth. A real aerial banner dwarfs
      // the plane dragging it — this one is ~3.5 plane-lengths of ribbon.
      // DoubleSide so the reverse reads (mirrored) just like the real thing.
      const rope = new THREE.Mesh(
        new THREE.BoxGeometry(4.2, 0.08, 0.08),
        new THREE.MeshLambertMaterial({ color: 0xd8d2bc }),
      );
      rope.position.set(-6.0, 0.3, 0);
      this.banner.add(rope);
      const cloth = new THREE.Mesh(
        new THREE.PlaneGeometry(32, 5),
        new THREE.MeshLambertMaterial({
          map: makeBannerTexture(this.ad, "towed"),
          side: THREE.DoubleSide,
        }),
      );
      cloth.position.set(-24, 0.3, 0);
      this.banner.add(cloth);
      this._cloth = cloth;
    } else {
      // A lit marquee board per flank, each rotated so its face (and the
      // writing) reads correctly from that side of the hull. Sized to sit
      // INSIDE the hull's flat center section (12 long, radius 3.1) — the
      // board mustn't spill onto the tapered nose and tail cones.
      this.banner.add(
        new THREE.Mesh(
          mergeBoxes(marqueeBoardBoxes()),
          // Its own material instance, not the shared planeMaterial: a banner
          // swap at the bell disposes everything under this group.
          new THREE.MeshLambertMaterial({ vertexColors: true }),
        ),
      );
      const tex = makeBannerTexture(this.ad, "sides");
      for (const s of [1, -1]) {
        const panel = new THREE.Mesh(
          new THREE.PlaneGeometry(10.5, 2.6),
          // The board lights its own poster — a marquee whose copy goes dark
          // after sunset is a marquee the sponsor asks for a refund on. Gentle
          // enough that it reads as bulb-wash at noon, not as a lightbox.
          new THREE.MeshLambertMaterial({
            map: tex, emissiveMap: tex, emissive: 0xffffff, emissiveIntensity: 0.42,
          }),
        );
        panel.position.set(0, MQ_CY, s * (MQ_FACE_Z + 0.02));
        panel.rotation.y = s > 0 ? 0 : Math.PI;
        this.banner.add(panel);
      }
      this._buildMarqueeLights();
      this._cloth = null;
    }
    this._placeBanner();
    this.group.add(this.banner);
  }

  // Seat the banner on whichever airframe is mounted: the model Skywriter's
  // tow hook sits half a metre aft of the box one's, and the model Marquee's
  // gasbag is fatter (3.35 m against 3.1), so its boards move out with it.
  _placeBanner() {
    if (!this.banner) return;
    const spec = this.rig?.spec;
    this.banner.position.set(0, 0, 0);
    this.banner.scale.set(1, 1, 1);
    if (spec?.towHook) this.banner.position.x = spec.towHook[0] + 3.9;
    if (spec?.envelopeR) this.banner.scale.z = (3.02 + (spec.envelopeR - 3.1)) / 3.02;
  }

  // Ring the sign with running bulbs: one instanced mesh for the filaments and
  // one for their additive halos, both flanks in the same pair of draw calls.
  // Bulb k of the ring is the same k on both faces, so the chase runs the same
  // way round whichever side of the zeppelin you're looking at.
  _buildMarqueeLights() {
    const ring = marqueeRing();
    const n = ring.length * 2;
    const bulbs = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.3, 0.3, 0.26),
      // fog:false so the sign still reads across the haze — a marquee nobody
      // can make out from the furball is a marquee nobody paid for. The per-
      // bulb color rides instanceColor (the house pattern, see effects.js).
      new THREE.MeshBasicMaterial({ color: 0xffffff, fog: false }),
      n,
    );
    bulbs.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);
    const halos = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.9, 0.9, 0.12),
      new THREE.MeshBasicMaterial({
        color: 0xffffff, transparent: true, opacity: 0.5,
        blending: THREE.AdditiveBlending, depthWrite: false, fog: false,
      }),
      n,
    );
    halos.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);
    const m = new THREE.Matrix4();
    let i = 0;
    for (const s of [1, -1]) {
      for (const [x, y] of ring) {
        // rotation.y = PI on the port face mirrors x as well as z.
        m.makeTranslation(s * x, y, s * (MQ_FACE_Z + 0.12));
        bulbs.setMatrixAt(i, m);
        m.makeTranslation(s * x, y, s * (MQ_FACE_Z + 0.16));
        halos.setMatrixAt(i, m);
        i++;
      }
    }
    this.banner.add(bulbs, halos);
    this._mq = {
      bulbs, halos, ring: ring.length, t: Math.random() * 4, // desync craft
      // Every marquee has one bulb the electrician keeps promising to fix.
      dud: Math.floor(Math.random() * ring.length),
    };
    this._stepMarquee(0);
  }

  // Advance the chase one frame. Cheap enough to run whenever the sign is
  // legible; past that the bulbs simply hold their last pattern.
  _stepMarquee(dt) {
    const mq = this._mq;
    mq.t += dt;
    const head = mq.t * MQ_CHASE_HZ;
    for (let i = 0; i < mq.ring; i++) {
      const phase = (((i - head) % MQ_CHASE_GAP) + MQ_CHASE_GAP) % MQ_CHASE_GAP;
      let on = phase < 1;
      if (i === mq.dud && Math.random() < 0.3) on = false; // the flickering one
      mq.bulbs.setColorAt(i, on ? MQ_LIT : MQ_DIM);
      mq.bulbs.setColorAt(i + mq.ring, on ? MQ_LIT : MQ_DIM);
      mq.halos.setColorAt(i, on ? MQ_HALO : MQ_OFF);
      mq.halos.setColorAt(i + mq.ring, on ? MQ_HALO : MQ_OFF);
    }
    mq.bulbs.instanceColor.needsUpdate = true;
    mq.halos.instanceColor.needsUpdate = true;
  }

  /** Hide/show the floating callsign (the local gunner rides right beside it). */
  setNameplateVisible(v) {
    if (this.nameplate) this.nameplate.visible = v;
  }

  // The floating callsign as this pilot currently wears it. A badge still
  // decoding redraws the plate when it lands (unless the plane's long gone).
  _makeNameplate() {
    const ins = this.ins;
    return makeNameplate(this.name, this.team, this._nemesis, this.title, ins, () => {
      if (!this._disposed && this.ins === ins) this._rebuildNameplate();
    });
  }

  // Rebuild the floating callsign in place, preserving visibility. Shared by the
  // nemesis tint (#21), the career-title line (#26) and the unit insignia.
  _rebuildNameplate() {
    if (this.isSelf || !this.nameplate) return;
    const vis = this.nameplate.visible;
    this.group.remove(this.nameplate);
    this.nameplate.material.map?.dispose();
    this.nameplate.material.dispose();
    this.nameplate = this._makeNameplate();
    this.nameplate.visible = vis;
    this.group.add(this.nameplate);
  }

  /** Swap the unit insignia at the left of the callsign ("" = none). */
  setInsignia(ins) {
    ins = ins || "";
    if (this.ins === ins) return;
    this.ins = ins;
    this._rebuildNameplate();
  }

  /** Tint the callsign orange while this pilot is the viewer's nemesis (roadmap #21). */
  setNemesis(on) {
    on = !!on;
    if (this._nemesis === on) return;
    this._nemesis = on;
    this._rebuildNameplate();
  }

  /** Add/refresh the career-title line under the callsign (roadmap #26). */
  setTitle(title) {
    title = title || "";
    if (this.title === title) return;
    this.title = title;
    this._rebuildNameplate();
  }

  /** Repaint the airframe with a new scheme (roadmap #25), keeping nameplate,
   * markers, props and turrets intact. */
  setScheme(scheme) {
    scheme = scheme | 0;
    if (scheme === this.scheme) return;
    this.scheme = scheme;
    this.body.geometry = planeGeometry(this.type, this.scheme);
    this.rig?.paint(this.team, schemePaint(SCHEMES[scheme], this.type));
    this._buildStripe();
    this._dmgSig = null;      // force the damage overlay to re-evaluate
    this._lostKey = undefined; // and the section-loss body variant
  }

  /** Stencil kill marks under the cockpit — one per kill this round, capped at
   * 20 (roadmap #25). Rebuilds only when the count changes; culled by distance
   * in update(). */
  setKillMarks(n) {
    n = Math.max(0, Math.min(20, n | 0));
    if (n === this._killN) return;
    this._killN = n;
    if (this.killMarks) {
      this.group.remove(this.killMarks);
      this.killMarks.geometry.dispose();
      this.killMarks = null;
    }
    if (n <= 0) return;
    const a = this._anchors();
    // Proud of the starboard fuselage skin so it reads: a model's anchor IS
    // the skin, the box anchors sit a little inside theirs.
    const z = a.fuse.z + (this.rig ? 0.03 : 0.16);
    const x0 = a.canopy.x + 0.2; // just forward of the cockpit
    const boxes = [];
    const per = 10;
    for (let i = 0; i < n; i++) {
      const row = Math.floor(i / per), col = i % per;
      boxes.push({
        x: x0 - col * 0.28, y: a.fuse.y + 0.28 - row * 0.32, z,
        w: 0.22, h: 0.26, d: 0.05, color: 0x15171b,
      });
    }
    this.killMarks = new THREE.Mesh(mergeBoxes(boxes), damageMaterial);
    this.killMarks.visible = false; // update() reveals it up close
    this.group.add(this.killMarks);
  }

  _buildAce() {
    const spin = new THREE.Mesh(aceSpinner(), planeMaterial);
    const pennant = new THREE.Mesh(acePennant(), planeMaterial);
    const hub = this.rig?.propDiscs[0];
    const fin = this.rig?.anchors.fins[0];
    if (hub) {
      // Capping the model's own spinner.
      spin.position.set(hub.pos.x + hub.hub * 0.7, hub.pos.y, hub.pos.z);
    } else {
      const a = this._anchors();
      spin.position.set(a.engine.x + 0.5, a.engine.y, a.engine.z);
    }
    if (fin) {
      // The staff stands on the crown of the fin (the anchor's x rides 28% back
      // from its leading edge; the crown is mid-chord), not half a metre over it.
      pennant.position.set(fin.x - fin.w * 0.73, fin.top + 0.2, fin.z);
    } else {
      const f = (TAIL_STRIPE_ANCHORS[this.type] ?? [])[0] ?? { x: -4.5, y: 1.6, z: 0 };
      pennant.position.set(f.x, f.y + 1.15, f.z);
    }
    this._aceMeshes = [spin, pennant];
    for (const m of this._aceMeshes) {
      m.visible = false; // update() reveals it inside the marker range
      this.group.add(m);
    }
  }

  /** Gold spinner cap + fin pennant marking a career ace (roadmap #26). */
  setAce(on) {
    on = !!on;
    if (on === this._ace) return;
    this._ace = on;
    if (on) {
      this._buildAce();
    } else {
      for (const m of this._aceMeshes ?? []) this.group.remove(m);
      this._aceMeshes = null;
    }
  }

  /**
   * Aim one crew turret from snapshot (or locally predicted) state. Pass
   * rows [{seat, yaw, pitch, firing}] for every manned seat; seats not named
   * stow. Call setTurretAim(seat, yaw, pitch, firing) to drive one seat
   * directly (the local gunner's prediction).
   */
  setTurrets(rows) {
    if (!this.turrets?.length) return;
    for (const t of this.turrets) {
      t.manned = false;
      t.firing = false;
    }
    for (const r of rows ?? []) this.setTurretAim(r.seat, r.yaw, r.pitch, r.firing);
  }

  setTurretAim(seat, yaw, pitch, firing) {
    const t = this.turrets?.[seat];
    if (!t) return;
    t.manned = true;
    t.tgtYaw = yaw;
    t.tgtPitch = pitch;
    t.firing = !!firing;
  }

  setType(type) {
    if (type !== this.type) this.buildType(type);
  }

  update(dt, firing, camDist) {
    // Career detailing (roadmap #25/#26) is a reward you notice after the merge,
    // not a beacon before it: kill marks read only up close, the ace pennant and
    // spinner out to the nameplate's legibility range.
    if (camDist !== undefined) {
      if (this.killMarks) this.killMarks.visible = camDist < 120;
      if (this._aceMeshes) for (const m of this._aceMeshes) m.visible = camDist < 400;
      // The side a plane flies for rides its nameplate alone: past
      // NAMEPLATE_TRUE_M the tile holds its size on screen (and its height
      // over the airframe) instead of dwindling with the airplane.
      const np = this.nameplate;
      if (np) {
        const k = Math.max(1, camDist / NAMEPLATE_TRUE_M), b = np.userData.base;
        np.scale.set(b.w * k, b.h * k, 1);
        np.position.y = b.y * k;
      }
    }
    // The prop answers the ENGINE, not the throttle lever: a shot-out engine
    // windmills lazily in the slipstream no matter how hard the pilot pushes
    // the handle — the spin-down IS the engine-out telling (no HUD needed).
    const eng = this.engineHealth ?? 1;
    const engRun = eng <= 0.02 ? 0.06 : 0.45 + 0.55 * eng;
    const spin = (12 + this.throttle * 55) * engRun;
    for (const prop of this.props) {
      prop.rotation.x += dt * spin;
    }
    this.rig?.spin(dt, spin);
    // The disc fades with the engine too: a windmilling prop is a faint blur,
    // not the solid ghost of one under power (same engine-out telling). From
    // the seat (cockpit.js) it's a metre or two off the eye and fills half the
    // windscreen: there it's the shimmer a pilot looks through, a third as thick.
    this.discMat.opacity = (0.07 + this.throttle * 0.16) * engRun * (this.interior ? 0.3 : 1);
    if (this._cloth) {
      // Towed cloth flutters: a lazy ripple, a slight trailing sag. The
      // rotations pivot at the cloth's center, so on a ribbon this long even
      // small angles read as a big, slow wave at the trailing edge.
      this._bannerT += dt;
      this._cloth.rotation.x = Math.sin(this._bannerT * 2.6) * 0.05;
      this._cloth.rotation.y = Math.sin(this._bannerT * 1.7) * 0.035;
      this._cloth.position.y = 0.3 + Math.sin(this._bannerT * 2.1) * 0.25;
    }
    // The zeppelin's sign runs its lights while anyone is close enough to read
    // the copy; beyond that the bulbs hold their last pattern (the ring is a
    // few pixels of gold from there anyway).
    if (this._mq && (camDist === undefined || camDist < 2200)) this._stepMarquee(dt);
    if (this.jets.length) {
      // Afterburner: the cone stretches aft and flickers with the throttle;
      // the shock diamonds only light once the burner is really cooking.
      // A dying turbojet's flame shrinks with it (same engine-out telling).
      const th = this.throttle * engRun;
      const len = 0.6 + th * (2.6 + Math.random() * 0.7);
      const w = 0.55 + th * 0.65;
      this.jetMat.opacity = 0.2 + th * 0.4;
      this.jetCoreMat.opacity = th > 0.5 ? Math.min(0.95, (th - 0.5) * 2.1) : 0;
      for (const jet of this.jets) {
        jet.scale.set(len, w, w);
        for (const d of jet.userData.diamonds) d.visible = Math.random() > 0.12;
      }
    }
    if (firing !== undefined) {
      // The whole battery fires as one synchronized volley — pair, quad, or
      // lone gatling — so flash every barrel in sync (shared flicker keeps
      // them all lit on the same frames).
      const lit = firing && Math.random() > 0.25;
      for (const f of this.flashes) f.visible = lit;
    }
    // Ease each crew turret toward its aim (a manned target, or the stow
    // position once the seat empties), and flicker the twin muzzles.
    this.turrets?.forEach((t, seat) => {
      const wantYaw = t.manned ? t.tgtYaw : t.spec.stowYaw;
      const wantPitch = t.manned ? t.tgtPitch : t.spec.stowPitch;
      const k = 1 - Math.exp(-12 * dt);
      t.yaw += Math.atan2(Math.sin(wantYaw - t.yaw), Math.cos(wantYaw - t.yaw)) * k;
      t.pitch += (wantPitch - t.pitch) * k;
      t.yawPivot.rotation.y = t.yaw;
      t.pitchPivot.rotation.z = t.pitch;
      const lit = t.manned && t.firing && Math.random() > 0.35;
      for (const f of t.flashes) f.visible = lit;
      if (this.rig) {
        this.rig.poseTurret(seat, t.yaw, t.pitch);
        if (t.rigAim) {
          t.rigAim.yawPivot.rotation.y = t.yaw;
          t.rigAim.pitchPivot.rotation.z = t.pitch;
          for (const f of t.rigAim.flashes) f.visible = lit;
        }
      }
    });
  }

  // Show visible battle damage for a component-damage state. Cheap to call
  // every snapshot: it buckets the health values and only rebuilds the overlay
  // mesh when the bucket actually changes. d = { engine, wingL, wingR, hp }.
  applyDamage(d = {}) {
    // The prop and the burner plume read the engine's health every frame
    // (see update()): a dead engine windmills instead of screaming.
    this.engineHealth = clamp01(d.engine ?? 1);
    this._lastDmg = d;
    const q = (v) => Math.round(clamp01(v ?? 1) * 5); // 6 buckets per component
    const sig = `${q(d.engine)}.${q(d.wingL)}.${q(d.wingR)}.${q(d.hp)}.${q(d.fuel)}.${q(d.pilot)}.${q(d.tail)}`;
    if (sig === this._dmgSig) return;
    this._dmgSig = sig;

    // Whole-section losses: swap to a body with the section sheared off, and
    // shed the torn-away piece into the world the moment it goes.
    const lost = {
      wingL: (d.wingL ?? 1) <= WING_LOSE,
      wingR: (d.wingR ?? 1) <= WING_LOSE,
      canopy: (d.pilot ?? 1) <= CANOPY_LOSE,
      tail: (d.tail ?? 1) <= TAIL_LOSE,
    };
    // The model's wounds: kit swaps, scorch, and holes on the real skin.
    this.rig?.applyDamage(d, lost, this.entityId ?? 0);
    // ...which just re-showed a canopy the pilot is sitting inside.
    this._applyInterior();
    const lostKey = `${lost.wingL ? 1 : 0}${lost.wingR ? 1 : 0}${lost.canopy ? 1 : 0}${lost.tail ? 1 : 0}`;
    if (lostKey !== this._lostKey) {
      const any = lost.wingL || lost.wingR || lost.canopy || lost.tail;
      this.body.geometry = any ? damagedBody(this.type, lost, this.scheme) : planeGeometry(this.type, this.scheme);
      // Shed only on a fresh tear (skip the first apply, so spawning or staging
      // an already-damaged plane doesn't fling phantom wreckage).
      const prev = this._lostKey;
      if (prev !== undefined) {
        this.group.updateMatrixWorld();
        if (lost.wingR && prev[1] === "0") this._shed("wingR");
        if (lost.wingL && prev[0] === "0") this._shed("wingL");
        if (lost.canopy && prev[2] === "0") this._shed("canopy");
        if (lost.tail && (prev[3] ?? "0") === "0") this._shed("tail");
      }
      this._lostKey = lostKey;
    }

    if (this.damage) {
      this.boxLevel.remove(this.damage);
      this.damage.geometry.dispose();
      this.damage = null;
    }
    const boxes = damageBoxes(this.type, d, this.entityId ?? 0);
    if (!boxes.length) return;
    this.damage = new THREE.Mesh(mergeBoxes(boxes), damageMaterial);
    this.boxLevel.add(this.damage);
  }

  // Tear a section off into tumbling world wreckage, kicked clear of the plane.
  _shed(kind) {
    const axis = kind === "canopy"
      ? new THREE.Vector3(0, 1, 0)
      : kind === "tail"
        ? new THREE.Vector3(-1, 0.4, 0) // the empennage falls away aft
        : new THREE.Vector3(0, 0, kind === "wingL" ? -1 : 1);
    const vel = axis.applyQuaternion(this.group.quaternion).multiplyScalar(6).add(new THREE.Vector3(0, -2, 0));
    // The model's own torn piece when one is mounted (and the kit knows it).
    const piece = this.rig?.debrisFor(kind, this.group.matrixWorld);
    if (piece) {
      shedDebris(this.scene, piece, null, null, vel);
      return;
    }
    const local = sectionBreakPoint(this.type, kind);
    const pos = this.group.localToWorld(local.clone());
    shedDebris(this.scene, sectionDebrisGeo(this.type, kind, this.scheme), pos, this.group.quaternion.clone(), vel);
  }

  setBlink(on) {
    // Spawn-protection shimmer.
    this.lod.visible = !on || Math.floor(performance.now() / 90) % 2 === 0;
  }

  dispose(scene) {
    this._disposed = true;
    scene.remove(this.group);
    this.rig?.dispose();
    this.damage?.geometry.dispose();
    this.stripe?.geometry.dispose();
    this.discMat?.dispose();
    this.jetMat?.dispose();
    this.jetCoreMat?.dispose();
    this.group.traverse((o) => {
      if (o.isSprite) {
        o.material.map?.dispose();
        o.material.dispose();
      }
    });
  }
}
